// @ts-nocheck
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
  useLocation,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck,
  Timer,
  Camera,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowRight,
  Clock,
  Loader2,
  HelpCircle,
  RotateCcw,
  PlayCircle,
  Check,
  UserCheck,
  ChevronRight,
  ChevronLeft,
  XOctagon,
  CalendarClock,
  ShieldAlert,
  Lock,
  Info,
  Monitor,
  Search,
  FilterX,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { CardSkeleton } from "@/components/loading";
import {
  fetchActiveExams,
  fetchAllPastExamSessions,
  fetchSessionQuestions,
  STUDENT_QUERY_OPTIONS,
  STUDENT_QUERY_KEYS,
  invalidateAllStudentState,
  fetchSessionState,
  createExamSession,
  saveExamResponse,
  saveEssayResponse,
  completeExamSession,
  terminateExamSession,
  uploadBiometricSelfie,
  fetchSessionReview,
} from "@/lib/api";
import { sendExamHeartbeat, sendDiagnosticEvent } from "@/lib/api/exams";
import {
  enqueueViolation,
  flushViolations,
  onViolationAck,
  resumeViolationQueue,
} from "@/lib/proctoring/violation-queue";
import {
  captureAndQueue,
  captureFrame,
  queueSnapshot,
  flushSnapshots,
  clearSnapshotQueue,
} from "@/lib/proctoring/snapshot-uploader";
import {
  probeDevTools,
  isDevToolsLikelyOpen,
  noteFullscreenBaseline,
  clearFullscreenBaseline,
  isSecondaryMonitorActive,
  getMonitorStatus,
  onDisplayChange,
} from "@/lib/proctoring/devtools-detector";
import { checkDeviceEligibility } from "@/lib/proctoring/device-gate";
import {
  acquireCamera,
  releaseCamera,
  retryCamera,
  attachCamera,
  useCameraState,
  isTerminalCameraError,
} from "@/lib/proctoring/camera";
import { ExamWatermark } from "@/components/exams/ExamWatermark";
import type { CompleteExamResult } from "@/lib/api/exams";
import type { Exam, ExamQuestion } from "@/lib/types";
import { fetchAvailableExams } from "@/lib/api";
import { resolveAutoSelectedExam } from "@/lib/exams/auto-select";
import { humanizeGradingStatus } from "@/lib/exam-status-labels";
import { fetchRecentlyIssuedCertificates } from "@/lib/api/certificates";
import { useExamOfflineGuard, clearExamDraft } from "@/hooks/use-exam-offline-guard";

interface ExamSearch {
  examId?: string;
  // `active` marks an in-progress exam attempt. It MUST be declared here and
  // preserved below: validateSearch is the whitelist for this route's search
  // params, and dropping `active` made the sync effect further down navigate to
  // ?active=true, read it back as absent, and navigate again on every render.
  // It also kept _authenticated.tsx from ever hiding the sidebar/header mid-exam.
  active?: true;
}

export const Route = createFileRoute("/_authenticated/exams")({
  validateSearch: (search: Record<string, unknown>): ExamSearch => {
    return {
      examId: search.examId as string | undefined,
      // Normalise to `true` or absent, so the string form that survives a page
      // reload and the boolean form written by navigate() compare identically.
      active: search.active === true || search.active === "true" ? true : undefined,
    };
  },
  component: ExamsPage,
});

/**
 * Hard ceiling on how long a student waits for proctoring/answer data to flush before their exam is
 * submitted. One shared budget for everything on that path, not a per-step timeout — per-step timeouts
 * add up, and the sum is what the student actually experiences.
 *
 * 6s is chosen against what the wait is for: these requests are small and already in flight, so on any
 * working connection they finish in well under a second, and the budget is only ever reached when the
 * connection is genuinely broken. In that case waiting longer does not help — the violation queue is
 * durable and will deliver on the next page, and the server's heartbeat-gap flag already marks a session
 * whose client went silent. So the ceiling costs nothing on a good connection and prevents a bad one from
 * blocking submission.
 */
const PRE_SUBMIT_FLUSH_BUDGET_MS = 6_000;

/**
 * How much the viewport must shrink before a resize counts as a violation, as a fraction of the size
 * recorded when the attempt went fullscreen.
 *
 * Not zero, because harmless things move the viewport by a few pixels: a mobile URL bar collapsing, a
 * software keyboard opening over an essay field, a scrollbar appearing. 5% is comfortably above that
 * noise and far below any window size a student could actually read a second window alongside.
 */
const RESIZE_VIOLATION_THRESHOLD = 0.05;

/**
 * Window in which a resize is treated as part of a fullscreen exit rather than a separate offence.
 *
 * Leaving fullscreen necessarily fires `resize` too, so without this every exit would be logged twice
 * and burn two of the student's three allowed violations for one action.
 */
const FULLSCREEN_EXIT_RESIZE_GRACE_MS = 1_500;

/**
 * Settling window after the browser reports ENTRY into fullscreen, during which a viewport shrink is
 * read as the transition finishing rather than as a student resizing their window.
 *
 * The entry side needs this as much as the exit side above. `fullscreenchange` fires when the browser
 * has decided to change modes, not when the compositor has finished: the viewport can report an
 * intermediate size for a frame or two, and on platforms that overlay a "press Esc to exit" banner or a
 * permission bar it can briefly report a genuinely smaller height before settling. Measuring a shrink
 * against a baseline captured in the middle of that is measuring the animation, not the student.
 */
const FULLSCREEN_ENTER_RESIZE_GRACE_MS = 1_500;

/**
 * How long after the attempt goes active before window blur, viewport shrink and fullscreen exit start
 * counting as violations.
 *
 * This exists because of what production actually did to students. Starting an attempt fires a burst of
 * events that look exactly like evasion and are not: `requestFullscreen()` moves focus, so the window
 * emits `blur`; the mode change emits `resize`; and a request the browser declines or immediately
 * reverses — an expired user activation, a permission prompt, an OS-level interruption — emits
 * `fullscreenchange` with no fullscreen element. Each of those was logged as its own offence. With the
 * ceiling at three, a student could be auto-terminated in the same second they started, before a single
 * question had rendered. Session 49A2A8A6 was terminated one second after `StartedAt` with exactly this
 * triple: Focus Out, Window Resized, Exited Fullscreen. Fifteen sessions died within two seconds of
 * starting, and each one sent a Team Leader to undo it by hand.
 *
 * Four seconds is chosen against the thing being excluded, not as a round number: a fullscreen
 * transition plus the layout settling after it is a sub-second event on any machine that can run the
 * exam, and 4s covers a slow laptop several times over while staying far below the time it takes a
 * student to open anything worth opening. It is a grace on the *penalty* only — the fullscreen gate
 * still engages, the content still blurs, and the events themselves are still what drives the UI. And
 * it is deliberately not applied to `visibilitychange`, so a student who genuinely switches tabs in the
 * first four seconds is still caught by the tab-switch detector.
 */
const PROCTORING_STARTUP_GRACE_MS = 4_000;

/**
 * Window within which the "student left the exam surface" detectors collapse to a single violation.
 *
 * Four separate detectors watch one physical act. Alt-tabbing out of a fullscreen exam fires `blur`
 * (Focus Out), `visibilitychange` (Tab Switch), `fullscreenchange` (Exited Fullscreen) and `resize`
 * (Window Resized) — four events, one decision by the student, and until now four independent charges
 * against an allowance of three. That is why the production storm reached the ceiling in a single
 * second: not because a detector misfired, but because leaving once was billed as leaving three times.
 *
 * Collapsing them is not leniency. A student who leaves three times is still terminated on the third
 * departure, which is what "three violations" was always meant to mean. What it removes is the case
 * where the first departure is also the last, and the codebase already accepted this reasoning pairwise
 * for fullscreen-exit versus resize (`FULLSCREEN_EXIT_RESIZE_GRACE_MS`) — this generalises that
 * judgement to the whole family rather than leaving two of the four detectors outside it.
 *
 * 1.5s matches the existing pairwise grace. It is long enough to span the event cascade a single act
 * produces (they arrive within a few frames of each other) and far too short to cover a second, separate
 * departure.
 */
const FOCUS_LOSS_COALESCE_MS = 1_500;

// Detect mobile phones (not tablets) via user-agent. Tablets/iPads/Desktops allowed.
function isMobilePhoneUA(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isiPad = /iPad/i.test(ua) || (/(Macintosh)/i.test(ua) && "ontouchend" in document);
  if (isiPad) return false;
  const isTablet = /Tablet|PlayBook|Silk|Kindle|Nexus 7|SM-T|GT-P/i.test(ua);
  if (isTablet) return false;
  return /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|BB10|Opera Mini|IEMobile/i.test(ua);
}

function ExamsPage() {
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Route State Machine
  const [phase, setPhase] = useState<
    "selection" | "biometric" | "preflight" | "active" | "terminated" | "result"
  >("selection");
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);

  // Selection-view search & filters. Purely client side — the available-exams list is already
  // fully loaded for the student, so narrowing it never costs a round trip.
  const [examSearch, setExamSearch] = useState("");
  const [examStatusFilter, setExamStatusFilter] = useState<string>("all");
  const [examTypeFilter, setExamTypeFilter] = useState<string>("all");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Active Exam State
  const [sessionId, setSessionId] = useState<string>("");
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [textAnswers, setTextAnswers] = useState<Record<string, string>>({});
  const [examTimeRemaining, setExamTimeRemaining] = useState<number>(0);
  const [violations, setViolations] = useState<number>(0);
  /**
   * The authoritative local violation count.
   *
   * A ref, not the `violations` state, because incrementing this count has SIDE EFFECTS attached —
   * enqueuing a durable violation, capturing a webcam frame, possibly terminating the attempt. Deriving
   * the next count inside a `setViolations(prev => ...)` updater put those effects inside a function
   * React is free to call more than once for a single dispatch (StrictMode in development, or update
   * re-basing under concurrent rendering). Each replay would mint a NEW clientEventId, so the server's
   * idempotency key — which dedupes by that id — could not collapse them, and one tab switch would be
   * counted as two or three against a student.
   *
   * A ref increments exactly once per call, synchronously, in a plain function. `violations` state is
   * now only a mirror of this for rendering. Keep the two written together.
   */
  const violationCountRef = useRef<number>(0);
  /** Sets the local count and its render mirror as one operation, so they cannot drift. */
  const setViolationCount = (n: number) => {
    violationCountRef.current = n;
    setViolations(n);
  };
  // Debounced essay autosave timer (per question) so answers persist while typing,
  // not only on blur — protects against a crash/timeout before the field loses focus.
  const essaySaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Latest answer/question state mirrored into refs. The proctoring event handlers are
  // registered in an effect keyed on [phase, sessionId, selectedExam], so they capture
  // a stale `textAnswers`/`questions`; flushing before an emergency lock reads the refs
  // to persist the true latest answers.
  const textAnswersRef = useRef<Record<string, string>>({});
  const questionsRef = useRef<ExamQuestion[]>([]);
  useEffect(() => {
    textAnswersRef.current = textAnswers;
  }, [textAnswers]);
  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);

  // Exam Review State
  const [reviewSessionId, setReviewSessionId] = useState<string | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewExamPolicy, setReviewExamPolicy] = useState<string>("Never");
  const [reviewExamTitle, setReviewExamTitle] = useState<string>("");

  const handleOpenReview = (examId: string, sessionId: string, policy: string, title: string) => {
    setReviewSessionId(sessionId);
    setReviewExamPolicy(policy);
    setReviewExamTitle(title);
    setReviewDialogOpen(true);
  };

  const { data: review, isLoading: reviewLoading } = useQuery({
    queryKey: STUDENT_QUERY_KEYS.sessionReview(reviewSessionId),
    queryFn: () => fetchSessionReview(reviewSessionId!),
    enabled: !!reviewSessionId,
    ...STUDENT_QUERY_OPTIONS,
  });

  const getQuestionTimeLimit = (q: ExamQuestion) => {
    if (q?.time_limit_seconds && q.time_limit_seconds > 0) {
      return q.time_limit_seconds;
    }
    if (selectedExam && questions.length > 0) {
      return Math.max(15, Math.round((selectedExam.time_limit_minutes * 60) / questions.length));
    }
    return 60; // fallback to 60s
  };
  const [showViolationWarning, setShowViolationWarning] = useState(false);
  const [result, setResult] = useState<CompleteExamResult | null>(null);

  // ── Device, monitor & DevTools gate state ───────────────────────────────
  const [deviceBlocked, setDeviceBlocked] = useState(false);
  const [deviceBlockReason, setDeviceBlockReason] = useState<string | null>(null);
  const [devToolsBlocked, setDevToolsBlocked] = useState(false);
  // Tracked separately from devToolsBlocked so the student is told the truth about which condition
  // stopped them. Conflating the two is what produced "close developer tools" for a docking station.
  const [secondaryMonitorBlocked, setSecondaryMonitorBlocked] = useState(false);
  const secondaryMonitorViolationRef = useRef(false);

  // Biometric state — the CAPTURE and UPLOAD lifecycle only. Camera availability is deliberately no
  // longer part of this machine: it belongs to lib/proctoring/camera.ts, which owns the single stream
  // the whole attempt shares. Folding device state in here is what let "camera_error" be reached from
  // four separate acquisitions and then be indistinguishable from a failed selfie upload.
  const videoRef = useRef<HTMLVideoElement>(null);
  type BiometricStatus =
    | "idle" // camera live, awaiting first scan
    | "capturing" // scan animation running, frame not yet captured
    | "uploading" // frame captured, upload in flight
    | "verified" // capture + upload succeeded
    | "error"; // capture or upload failed — terminal until the user explicitly retries
  const [biometricStatus, setBiometricStatus] = useState<BiometricStatus>("idle");
  const [biometricError, setBiometricError] = useState<string | null>(null);
  const [selfieBlob, setSelfieBlob] = useState<Blob | null>(null);
  const [selfieUrl, setSelfieUrl] = useState<string | null>(null);
  // Once verification succeeds for an exam it is remembered for the whole in-memory attempt, so the
  // camera can never reopen for that exam (belt-and-suspenders against any phase bounce).
  const verifiedForExamRef = useRef<string | null>(null);
  const [isFullscreenRequiredOpen, setIsFullscreenRequiredOpen] = useState(false);

  // ── Preflight camera readiness ────────────────────────────────────────────
  // No probe any more. The preflight screen takes a hold on the SAME stream the attempt will monitor
  // with, so "camera ready" on this screen is a statement about the exact device and stream the exam
  // is about to use — not a separate acquisition that proved a different one worked a second ago and
  // then closed it. See the derived flags below, and lib/proctoring/camera.ts for why.

  // When an admin reopens a finished session, the student must go through the full security
  // preflight (biometric + DevTools + monitor gate) before re-entering. This holds the session id
  // so the preflight "Start Exam Now" button can resume it (instead of creating a new session).
  // null when the flow is a fresh start or a normal mid-session resume.
  const [reopenedSessionId, setReopenedSessionId] = useState<string | null>(null);

  // ── Proctoring (active attempt) ─────────────────────────────────────────
  // The in-exam preview is a second VIEW of the one shared stream, not a second stream. It used to be
  // its own acquisition, opened after the biometric effect had just torn the device down — and on
  // Windows, where the OS releases a camera asynchronously, that reopen is the one that failed with
  // NotReadableError. Since the monitoring failure path was non-blocking, the attempt then ran to the
  // end with no frames at all. That is the bug this whole rework exists to remove.
  const proctorVideoRef = useRef<HTMLVideoElement>(null);

  // Cadence, grace window and watermark identity are the SERVER's to decide (see SessionStateDto).
  // Seeded with the policy defaults so a state fetch that fails does not leave the exam un-proctored.
  const [proctorConfig, setProctorConfig] = useState<{
    ipAddress: string | null;
    snapshotIntervalSeconds: number;
    heartbeatIntervalSeconds: number;
    cameraGraceSeconds: number;
  }>({
    ipAddress: null,
    snapshotIntervalSeconds: 30,
    heartbeatIntervalSeconds: 20,
    cameraGraceSeconds: 60,
  });

  // ── Mid-attempt camera loss ─────────────────────────────────────────────
  // Seconds left to restore a camera that dropped during the exam. null when the camera is healthy.
  // The exam clock keeps running throughout — blocking the questions is what makes the camera
  // mandatory, but pausing the timer as well would make disconnecting it worth doing.
  const [cameraGraceLeft, setCameraGraceLeft] = useState<number | null>(null);
  // True once loss handling has terminated the attempt, so the countdown cannot fire twice.
  const cameraTerminationRef = useRef(false);

  // Frames captured at the instant a violation fired, held until that violation's server-side id comes
  // back so the frame can be attached to it. Keyed by the queue's client event id.
  const pendingViolationFrames = useRef<Record<string, Blob>>({});

  // True while the attempt is out of fullscreen. Drives the content blur — a violation that only logs
  // an event still leaves the questions readable on a windowed screen next to a phone camera.
  const [isFullscreenActive, setIsFullscreenActive] = useState(true);

  // sessionId in a ref so timers and the unmount cleanup read the CURRENT session rather than the value
  // captured when the effect was created.
  const sessionIdRef = useRef<string>("");

  /** Viewport size treated as "correct" for this attempt — the baseline a resize is measured against. */
  const baselineViewportRef = useRef<{ width: number; height: number } | null>(null);
  /** When fullscreen was last exited, so the resize that exit causes is not double-counted. */
  const fullscreenExitAtRef = useRef<number>(0);
  /** When fullscreen was last entered, so the layout settling after it is not read as a shrink. */
  const fullscreenEnteredAtRef = useRef<number>(0);
  /** True while a resize violation is already standing, so one drag is not logged per resize event. */
  const resizeViolationStandingRef = useRef<boolean>(false);
  /**
   * When the attempt entered the active phase — the reference point for `PROCTORING_STARTUP_GRACE_MS`.
   *
   * A ref rather than state because every reader is an event handler that must see the current value
   * without the effect being torn down and re-armed, and because re-rendering on it would do nothing.
   */
  const proctoringArmedAtRef = useRef<number>(0);

  // Derived render flags — existing JSX conditions read these, so most JSX stays untouched.
  //
  // Every camera flag now comes from the shared owner, so the biometric screen, the preflight screen
  // and the active attempt all read the same truth about the same device. Previously each screen
  // reported on its own most recent acquisition, which is how "Camera ready" on preflight could be
  // followed a second later by an attempt with no camera at all.
  const camera = useCameraState();
  const cameraAccessError = camera.status === "error" || camera.status === "lost";
  const cameraStarting = camera.status === "acquiring" || camera.status === "idle";
  const cameraReady = camera.status === "ready";

  // Kept as a tri-state under the old name so the preflight JSX reads unchanged: true = ready,
  // false = definitively unavailable, null = still resolving.
  const preflightCameraReady: boolean | null = cameraReady
    ? true
    : cameraAccessError
      ? false
      : null;
  // The owner's message is already specific to the failure kind (denied vs. busy vs. insecure origin).
  // Do not substitute a generic one: telling a student to check settings that are fine, for a device
  // another application is holding, is what made this look like the student's problem.
  const preflightCameraError = camera.error?.message ?? null;

  const biometricScanning = biometricStatus === "capturing" || biometricStatus === "uploading";
  const biometricConfirmed = biometricStatus === "verified";
  const handleRetryCamera = () => {
    setBiometricError(null);
    void retryCamera();
  };
  const resetBiometric = () => {
    setBiometricStatus("idle");
    setBiometricError(null);
    setSelfieBlob(null);
    setSelfieUrl(null);
    verifiedForExamRef.current = null;
  };

  const { examId } = useSearch({ from: "/_authenticated/exams" });

  // ── Offline guard: buffer answers to localStorage when disconnected ────
  useExamOfflineGuard(phase === "active" ? sessionId : null, {
    selectedAnswers,
    textAnswers,
    onRestored: (draft) => {
      // Merge the draft back into state — the latest keystroke wins.
      setSelectedAnswers((prev) => ({ ...prev, ...draft.selectedAnswers }));
      setTextAnswers((prev) => ({ ...prev, ...draft.textAnswers }));
      toast.success("Connection restored — syncing your answers…");
    },
  });

  // Fetch available exams from unified backend source of truth
  const { data: availableExams = [], isLoading: isLoadingAvailable } = useQuery({
    queryKey: STUDENT_QUERY_KEYS.availableExams(user?.id),
    queryFn: fetchAvailableExams,
    enabled: !!user?.id,
    ...STUDENT_QUERY_OPTIONS,
  });

  // Fetch active exams
  const { data: activeExams = [], isLoading: isLoadingExams } = useQuery<Exam[]>({
    queryKey: STUDENT_QUERY_KEYS.activeExams,
    queryFn: () => fetchActiveExams(isAdmin),
    ...STUDENT_QUERY_OPTIONS,
  });

  // Fetch past session statuses for each exam
  const {
    data: pastSessions = {},
    isLoading: isLoadingPast,
    refetch: refetchPast,
  } = useQuery<Record<string, any>>({
    queryKey: STUDENT_QUERY_KEYS.pastExamSessions(user?.id),
    queryFn: async () => {
      const examsList = await fetchActiveExams(isAdmin);
      const examIds = examsList.map((ex) => ex.id);
      return fetchAllPastExamSessions(user!.id, examIds);
    },
    enabled: !!user?.id,
    ...STUDENT_QUERY_OPTIONS,
  });

  // Any exam sitting in the remedial rewatch loop. This used to substring-match "remedial" against
  // lock_reason, which holds the student-facing MESSAGE ("Rewatch required before retrying.") and
  // never contains that word — so the remedial banner could never appear. The server sends the state
  // as a dedicated flag; use it.
  const hasRemedialLock = availableExams.some((ae) => ae.requires_remedial_rewatch);

  // Drives the "Training Incomplete" banner, so it must consider ONLY progression locks. Keyed off
  // is_ready it fired for any lock at all, telling students to go finish training when the real
  // blocker was a spent attempt or a closed exam window.
  const isTrainingComplete =
    isAdmin ||
    !availableExams.some((ae) => ae.is_locked && ae.lock_reason_code === "ProgressionLocked");

  // Auto-select exam if examId search param is present
  useEffect(() => {
    if (phase !== "selection" || selectedExam || !examId) return;
    if (isLoadingAvailable || isLoadingExams || isLoadingPast || !user?.id) return;

    const outcome = resolveAutoSelectedExam({ examId, activeExams, availableExams, pastSessions });

    switch (outcome.kind) {
      case "passed":
        toast.info(outcome.message);
        break;
      case "locked":
        toast.error(outcome.message);
        break;
      case "unavailable":
        toast.error(outcome.message);
        break;
      case "start":
        setSelectedExam(outcome.exam as any);
        resetBiometric();
        setPhase(outcome.biometricRequired ? "biometric" : "preflight");
        break;
    }
  }, [
    examId,
    activeExams,
    availableExams,
    pastSessions,
    phase,
    selectedExam,
    isLoadingAvailable,
    isLoadingExams,
    isLoadingPast,
    user?.id,
  ]);

  // ── The one camera acquisition for the whole attempt ──────────────────────
  //
  // This replaces four separate `getUserMedia` call sites: the biometric gate, the preflight probe,
  // the Retry Camera button, and the monitoring stream. Each opened the same physical device, and the
  // biometric → preflight → active path ran them as *stop → acquire → stop → acquire* within seconds.
  // Windows releases a camera asynchronously, so the last of those — the only one that captures
  // frames — routinely lost the race and failed with NotReadableError. Because monitoring failure was
  // deliberately quiet, the attempt then completed with an empty evidence timeline. That is the
  // "no frames during exams" report.
  //
  // The dependency is a BOOLEAN, not `phase`, and that is the entire fix: it stays true across all
  // three phases, so the effect runs once, holds one stream, and tears down once at the end. A `phase`
  // dependency would restore the churn no matter how the acquisition itself were written.
  const cameraNeeded =
    !!selectedExam && (phase === "biometric" || phase === "preflight" || phase === "active");

  useEffect(() => {
    if (!cameraNeeded) return;
    void acquireCamera();
    return () => releaseCamera();
  }, [cameraNeeded]);

  // Both <video> elements are views of the ONE stream. Re-attaching when `camera.stream` changes
  // covers the element that mounts before the device finishes opening, and the element that remounts
  // when a phase swaps the subtree out from under it.
  useEffect(() => {
    if (phase !== "biometric") return;
    return attachCamera(videoRef.current);
  }, [phase, camera.stream]);

  useEffect(() => {
    if (phase !== "active") return;
    return attachCamera(proctorVideoRef.current);
  }, [phase, camera.stream]);

  // Surface an unrecoverable camera failure once, at the moment it becomes known, rather than leaving
  // the student to work it out from a black circle. Only terminal kinds get a toast: a busy device is
  // retried automatically by the owner and usually resolves before anyone could act on a message.
  const notifiedCameraErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!cameraNeeded) {
      notifiedCameraErrorRef.current = null;
      return;
    }
    const err = camera.error;
    if (!err || !isTerminalCameraError(err)) return;
    if (notifiedCameraErrorRef.current === err.message) return;
    notifiedCameraErrorRef.current = err.message;
    toast.error(err.message, { duration: 10_000 });
  }, [cameraNeeded, camera.error]);

  /**
   * Arms the startup grace the moment the attempt goes active.
   *
   * Declared ahead of every effect that reads it so the stamp is in place before `enterFullscreen()` is
   * called and before any proctoring listener is attached — React runs effects in declaration order, and
   * the fullscreen request below is the very thing whose fallout this window exists to absorb.
   *
   * Re-stamped on each entry into `active`, which is correct: a resumed or reopened attempt goes through
   * the same fullscreen transition as a fresh one and deserves the same settling window. It is not
   * re-stamped while the attempt is running, so it cannot be refreshed to buy a second free window.
   */
  const proctoringStartupGraceRef = useRef<boolean>(false);
  useEffect(() => {
    if (phase !== "active") return;
    proctoringArmedAtRef.current = Date.now();
    proctoringStartupGraceRef.current = true;
    const timer = setTimeout(() => {
      proctoringStartupGraceRef.current = false;
    }, PROCTORING_STARTUP_GRACE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  /**
   * True while the attempt is still inside its startup grace.
   *
   * Checks the timestamp as well as the flag rather than trusting the timer alone: a machine that
   * suspends mid-transition can leave the timeout unfired for far longer than the grace was meant to
   * last, and the whole point of this window is that it is short.
   */
  const inProctoringStartupGrace = () =>
    proctoringStartupGraceRef.current &&
    Date.now() - proctoringArmedAtRef.current < PROCTORING_STARTUP_GRACE_MS;

  /** When the last "student left the exam surface" violation was charged. See FOCUS_LOSS_COALESCE_MS. */
  const focusLossChargedAtRef = useRef<number>(0);

  /**
   * Claims the single violation a departure is worth, for whichever of the four detectors observes it
   * first. Returns false for the rest of the cascade that same act produces.
   *
   * Deliberately claim-on-call rather than a check the caller can repeat: the first detector to arrive
   * takes the charge and stamps the window in one indivisible step, so two handlers firing in the same
   * task cannot both conclude they are first.
   */
  const claimFocusLossViolation = () => {
    const now = Date.now();
    if (now - focusLossChargedAtRef.current < FOCUS_LOSS_COALESCE_MS) return false;
    focusLossChargedAtRef.current = now;
    return true;
  };

  // Tab switching / Proctoring focus tracking & Security blocking
  useEffect(() => {
    if (phase !== "active" || !sessionId) return;

    // ── Clipboard neutralisation via Selection API ─────────────────────────
    // navigator.clipboard.writeText("") requires the page to be focused (Permissions API) and
    // WILL throw a DOMException when called from a blur handler — because by definition the page
    // has already lost focus when blur fires. Instead we use the synchronous Selection API: a
    // hidden, empty, off-screen textarea is pre-selected, and document.execCommand("copy") copies
    // the empty selection onto the OS clipboard. execCommand works synchronously and does not
    // require focus. This is the ONLY reliable way to clear the clipboard from a blur handler.
    const clipNuke = document.createElement("textarea");
    clipNuke.value = " ";                       // single space — cannot copy empty string
    clipNuke.style.position = "fixed";
    clipNuke.style.left = "-9999px";
    clipNuke.style.top = "-9999px";
    clipNuke.style.opacity = "0";
    clipNuke.setAttribute("readonly", "");      // prevent virtual keyboard on mobile
    clipNuke.setAttribute("aria-hidden", "true");
    document.body.appendChild(clipNuke);

    /** Wipes the OS clipboard by copying an empty selection. Safe to call when the page is unfocused. */
    const nukeClipboard = () => {
      try {
        clipNuke.select();
        document.execCommand("copy");
      } catch { /* execCommand may be disabled, but that's a graceful degradation */ }
      // Also attempt the async API in case the page regains focus during the call.
      try { navigator.clipboard.writeText("").catch(() => {}); } catch {}
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Not startup-graced: hiding the tab is an unambiguous departure even in the first second, and
        // no part of entering fullscreen sets `document.hidden`. It is coalesced, though, because a tab
        // switch also blurs the window and may drop fullscreen.
        if (claimFocusLossViolation()) logViolation("Tab Switch / Window Minimized");
      }
    };

    const handleBlur = () => {
      // Entering fullscreen takes focus away from the document, so the attempt's own opening move fires
      // this handler. Suppressed for the startup grace only — the clipboard is still wiped, because that
      // costs the student nothing and the screenshot it defeats is just as real in second one.
      if (!inProctoringStartupGrace() && claimFocusLossViolation())
        logViolation("Focus Out / Window Blur");
      // Any external capture tool (Snipping Tool, PrtSc, screen recorder) causes a blur.
      // Wipe the clipboard so whatever was captured is replaced with nothing, and the content
      // blur (isFullscreenActive) already makes the questions unreadable in the screenshot.
      nukeClipboard();
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      logViolation("Right Click Context Menu Attempt");
      toast.error("Right-click is disabled during the exam.");
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl/Cmd key combinations
      if (e.ctrlKey || e.metaKey) {
        const key = e.key.toLowerCase();
        if (key === "c" || key === "v" || key === "x" || key === "a" || key === "u") {
          e.preventDefault();
          logViolation(`Keyboard Shortcut Blocked (Ctrl+${key.toUpperCase()})`);
          toast.error(`Ctrl+${key.toUpperCase()} is disabled during the exam.`);
          return false;
        }
      }

      // Check for Inspect Element keys
      if (e.key === "F12") {
        e.preventDefault();
        logViolation("F12 Inspect key blocked");
        toast.error("Developer tools are disabled during the exam.");
        return false;
      }

      // Ctrl + Shift + I/J/C/K (DevTools) and Win+Shift+S (Snipping Tool)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
        const key = e.key.toLowerCase();
        if (key === "i" || key === "j" || key === "c" || key === "k") {
          e.preventDefault();
          logViolation(`Inspect tools shortcut blocked (Ctrl+Shift+${key.toUpperCase()})`);
          toast.error("Developer tools shortcuts are disabled during the exam.");
          return false;
        }
        // Win+Shift+S — Snipping Tool (Windows). The OS captures before JS can prevent it,
        // but we log the violation and nuke the clipboard.
        if (key === "s") {
          e.preventDefault();
          logViolation("Snipping Tool Shortcut Attempt (Win+Shift+S)");
          toast.error("Screen capture tools are disabled during the exam.");
          nukeClipboard();
          return false;
        }
      }

      // ── Mac screenshot shortcuts ─────────────────────────────────────────
      // Cmd+Shift+3 (full screen), Cmd+Shift+4 (selection), Cmd+Shift+5 (screenshot panel)
      if (e.metaKey && e.shiftKey) {
        const key = e.key;
        if (key === "3" || key === "4" || key === "5") {
          e.preventDefault();
          logViolation(`Mac Screenshot Shortcut (Cmd+Shift+${key})`);
          toast.error("Screenshots are disabled during the exam.");
          nukeClipboard();
          return false;
        }
      }

      // PrintScreen key — Chrome fires keyup not keydown for this key, so we also listen on
      // keyup below. The screenshot is already taken by the OS before JS can react, so we
      // nuke the clipboard as mitigation.
      if (e.key === "PrintScreen") {
        e.preventDefault();
        logViolation("PrintScreen / Screenshot Attempt");
        toast.error("Screenshots are disabled during the exam.");
        nukeClipboard();
        return false;
      }
    };

    // Chrome fires PrintScreen on keyup, not keydown — catch it here too.
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "PrintScreen") {
        e.preventDefault();
        nukeClipboard();
      }
    };

    const handleCutCopyPaste = (e: Event) => {
      e.preventDefault();
      logViolation(`${e.type.toUpperCase()} attempt blocked`);
      toast.error(
        `${e.type.charAt(0).toUpperCase() + e.type.slice(1)}ing is disabled during the exam.`,
      );
    };

    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    document.addEventListener("copy", handleCutCopyPaste);
    document.addEventListener("cut", handleCutCopyPaste);
    document.addEventListener("paste", handleCutCopyPaste);

    return () => {
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("copy", handleCutCopyPaste);
      document.removeEventListener("cut", handleCutCopyPaste);
      document.removeEventListener("paste", handleCutCopyPaste);
      // Clean up the hidden clipboard textarea
      try { document.body.removeChild(clipNuke); } catch {}
    };
  }, [phase, sessionId, selectedExam]);

  const enterFullscreen = async () => {
    try {
      const element = document.documentElement;
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      }
    } catch (err) {
      console.error("Failed to enter fullscreen:", err);
    }
  };

  const exitFullscreen = async () => {
    try {
      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      if (isFullscreen) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (err) {
      console.error("Failed to exit fullscreen:", err);
    }
  };

  // Fullscreen management logic
  useEffect(() => {
    if (phase !== "active") return;

    // Request fullscreen immediately on active phase start
    enterFullscreen();

    // Give the browser a moment to enter fullscreen, if not show the prompt
    const checkTimer = setTimeout(() => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      if (!isFullscreen) {
        setIsFullscreenRequiredOpen(true);
        setIsFullscreenActive(false);
      } else {
        // Covers the case where the transition completed before this effect's listener was attached,
        // so `handleFullscreenChange` never ran and the DevTools reference was never taken.
        noteFullscreenBaseline();
      }
    }, 600);

    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );

      // Blur state is tracked separately from the modal because it does different work: the modal is the
      // gate that forces the student back into fullscreen, while the blur makes the questions unreadable
      // for however long they are out of it. Logging the exit without hiding the content left the exam
      // fully legible on a windowed screen — the exact state a second device is pointed at.
      setIsFullscreenActive(isFullscreen);

      if (!isFullscreen) {
        // Stamped before logging so the resize this exit is about to fire is recognised as part of the
        // same action rather than a second, independent offence.
        fullscreenExitAtRef.current = Date.now();
        // Outside fullscreen the viewport cannot be predicted, so the DevTools size heuristic is
        // inert until we are back in — drop its reference rather than let it go stale.
        clearFullscreenBaseline();
        // Only the penalty is suppressed during the startup grace, never the gate: the modal below and
        // the content blur above still engage, so a student whose fullscreen request was declined or
        // instantly reversed is put back into fullscreen rather than charged for the browser's decision.
        // This is the event that used to complete the fatal triple in the first second of an attempt.
        if (!inProctoringStartupGrace() && claimFocusLossViolation())
          logViolation("Exited Fullscreen Mode");
        setIsFullscreenRequiredOpen(true);
      } else {
        // Re-entering fullscreen re-establishes the size baseline and clears any standing resize
        // offence, so the next genuine shrink is logged again.
        fullscreenEnteredAtRef.current = Date.now();
        resizeViolationStandingRef.current = false;
        // Both baselines are taken one frame after the browser reports the transition, so what they
        // record is the settled fullscreen viewport rather than an intermediate size the compositor was
        // passing through. Reading `window.innerWidth` synchronously here captured that intermediate
        // size, and every later comparison was then measured against a number that never existed on
        // screen. The `?? window` fallback keeps a baseline even if the frame never arrives (a tab
        // backgrounded mid-transition never runs its rAF callback).
        baselineViewportRef.current = { width: window.innerWidth, height: window.innerHeight };
        requestAnimationFrame(() => {
          baselineViewportRef.current = { width: window.innerWidth, height: window.innerHeight };
          noteFullscreenBaseline();
        });
        setIsFullscreenRequiredOpen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      clearTimeout(checkTimer);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, [phase]);

  // Exit fullscreen when returning to selection/result page
  useEffect(() => {
    if (phase === "result" || phase === "selection") {
      exitFullscreen();
    }
  }, [phase]);

  // ── Proctoring: keep sessionId readable from timers ─────────────────────
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  /**
   * Window resize / minimise detection.
   *
   * Fullscreen-exit detection alone is not sufficient, which is the whole reason this exists. A student
   * can shrink the exam window and put a second window (notes, a chat, a search) beside it in ways the
   * Fullscreen API does not always report as an exit: OS window snapping, a drag of the window frame on a
   * platform that keeps the fullscreen element, and browser-chrome fullscreen (F11) which is a different
   * mode from `requestFullscreen`. In all of those the questions stay readable while `fullscreenchange`
   * stays silent.
   *
   * Three things stop this from firing on innocent behaviour, because a false violation costs a student a
   * third of their allowance:
   *
   * - A 5% threshold against a recorded baseline, so URL-bar collapse, a software keyboard and a
   *   scrollbar appearing are all ignored.
   * - A grace window after a fullscreen exit, since leaving fullscreen necessarily fires resize too and
   *   that action is already logged by the fullscreen handler.
   * - A "standing" latch, so dragging a window edge — which fires resize continuously — is one violation
   *   and not thirty.
   *
   * A zero or near-zero viewport is treated as minimised and logged as such: it is the same evasion with
   * a different shape, and it reads more clearly to a reviewer than "resized to 0x0".
   */
  useEffect(() => {
    if (phase !== "active" || !sessionId) return;
    if (typeof window === "undefined") return;

    // Baseline from the moment monitoring starts. Re-established on every fullscreen (re-)entry above.
    //
    // At this instant the fullscreen request is typically still in flight, so this first reading is the
    // WINDOWED size. That is harmless in itself — the viewport only grows from there, and this detector
    // only looks for shrinkage — but it is not the size the attempt will actually be sat at, so it is
    // replaced once the layout has settled (see below).
    baselineViewportRef.current = { width: window.innerWidth, height: window.innerHeight };
    resizeViolationStandingRef.current = false;

    // Re-baseline against whatever the layout ACTUALLY settled at, once the startup grace expires.
    //
    // The fullscreen handler takes its baseline one frame after the browser reports the transition,
    // which is the settled size on any browser that resizes before it fires the event — but not on one
    // that keeps moving for a few hundred milliseconds afterwards (a chrome bar collapsing, a
    // "press Esc to exit" overlay, a window animating onto a second display). Left uncorrected, the
    // reference for the WHOLE attempt is a size that was never really on screen, and the first
    // legitimate glance at the viewport reads as a shrink. By the time the grace is up nothing is in
    // flight, so this reading is the trustworthy one.
    //
    // The only case worth skipping is a fullscreen transition still in progress at that exact moment,
    // which the entry handler will re-baseline for itself a frame later.
    const settleTimer = setTimeout(() => {
      if (Date.now() - fullscreenEnteredAtRef.current < FULLSCREEN_ENTER_RESIZE_GRACE_MS) return;
      baselineViewportRef.current = { width: window.innerWidth, height: window.innerHeight };
      resizeViolationStandingRef.current = false;
    }, PROCTORING_STARTUP_GRACE_MS);

    let debounce: ReturnType<typeof setTimeout> | null = null;

    const evaluate = () => {
      // The mode change into fullscreen emits `resize` by definition, and the attempt requests it the
      // moment it goes active. Measuring during either window measures the transition.
      if (inProctoringStartupGrace()) return;
      if (Date.now() - fullscreenEnteredAtRef.current < FULLSCREEN_ENTER_RESIZE_GRACE_MS) return;

      const baseline = baselineViewportRef.current;
      if (!baseline || baseline.width === 0 || baseline.height === 0) return;

      const width = window.innerWidth;
      const height = window.innerHeight;

      // Minimised (or otherwise given no viewport at all).
      if (width === 0 || height === 0) {
        if (!resizeViolationStandingRef.current) {
          resizeViolationStandingRef.current = true;
          // Latched regardless of whether the charge lands, so a minimise that was coalesced into an
          // accompanying blur is still not re-evaluated on every subsequent resize event.
          if (claimFocusLossViolation()) logViolation("Window Minimized");
        }
        return;
      }

      const shrankWidth = (baseline.width - width) / baseline.width;
      const shrankHeight = (baseline.height - height) / baseline.height;
      const shrank = Math.max(shrankWidth, shrankHeight);

      if (shrank <= RESIZE_VIOLATION_THRESHOLD) {
        // Back to (or above) the expected size — clear the latch so a later shrink is caught.
        if (shrank <= 0) resizeViolationStandingRef.current = false;
        return;
      }

      // The fullscreen handler already logged this same action.
      if (Date.now() - fullscreenExitAtRef.current < FULLSCREEN_EXIT_RESIZE_GRACE_MS) return;

      if (resizeViolationStandingRef.current) return;
      resizeViolationStandingRef.current = true;

      if (!claimFocusLossViolation()) return;

      logViolation(
        `Window Resized (${width}x${height}, ${Math.round(shrank * 100)}% smaller than expected)`,
      );
    };

    // Debounced: a window drag emits resize on every frame, and evaluating per event would both burn CPU
    // and race the latch above.
    const handleResize = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(evaluate, 400);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      if (debounce) clearTimeout(debounce);
      clearTimeout(settleTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, [phase, sessionId]);

  /**
   * Reconciles the server's answer for every delivered violation.
   *
   * The queue may deliver a violation seconds or minutes after the handler that raised it returned, so
   * this is where the server's count and its termination verdict actually land. The server is the
   * authority on both — the local optimistic count exists only to lock the UI without waiting on a
   * round trip, and where the two disagree (a stale local count after a resume, a concurrent breach)
   * the server's number replaces it.
   *
   * It is also where a violation-triggered frame gets attached to its violation row: the id only exists
   * once the violation has been stored, which is necessarily after the frame was captured.
   */
  useEffect(() => {
    if (phase !== "active") return;

    return onViolationAck((ack) => {
      const activeSession = sessionIdRef.current;

      const held = pendingViolationFrames.current[ack.clientEventId];
      if (held) {
        delete pendingViolationFrames.current[ack.clientEventId];
        if (activeSession) {
          queueSnapshot(activeSession, held, "Violation", ack.violation_id ?? null);
        }
      }

      // The server's count replaces the local one. It is the authority: it survives a reload, counts
      // violations this page never saw, and has already deduped retries.
      if (ack.violation_count > 0) setViolationCount(ack.violation_count);

      if (ack.terminated) {
        setPhase("terminated");
        refetchPast();
        qc.invalidateQueries({ queryKey: ["available-exams", user?.id] });
      }
    });
  }, [phase, user?.id]);

  /**
   * Loads the server's proctoring configuration for this attempt and resumes any queue left over from a
   * previous page.
   *
   * The resume call is the recovery path for a violation that outlived its tab: it was persisted, the
   * page died before delivery, and nothing else would ever look at that record again.
   */
  useEffect(() => {
    if (phase !== "active" || !sessionId) return;

    resumeViolationQueue(sessionId);

    let cancelled = false;
    void fetchSessionState(sessionId)
      .then((state) => {
        if (cancelled || !state) return;
        setProctorConfig({
          ipAddress: state.client_ip_address,
          snapshotIntervalSeconds: state.snapshot_interval_seconds,
          heartbeatIntervalSeconds: state.heartbeat_interval_seconds,
          cameraGraceSeconds: state.camera_grace_seconds,
        });
      })
      .catch(() => {
        // Keep the seeded defaults. Failing to read the cadence must not mean proctoring is off.
      });

    return () => {
      cancelled = true;
    };
  }, [phase, sessionId]);

  /**
   * Periodic DevTools monitor during the active exam.
   *
   * Uses only the non-intrusive detectors — the fullscreen viewport shortfall and the console getter
   * trap. No debugger statement on a timer: that would halt the page in front of the student and
   * advertise the detection.
   *
   * A positive reading must repeat on two consecutive polls before it becomes a violation. Both
   * detectors are heuristics, and the single most likely time to get a spurious reading is during a
   * transition — a fullscreen change settling, a window moving between displays of different DPI,
   * the compositor mid-resize. Ten seconds of a panel being open is still caught; a one-frame
   * artifact is not, and that asymmetry is the right way round when the penalty is a third of the
   * student's violation allowance on an accusation they cannot disprove.
   */
  useEffect(() => {
    if (phase !== "active" || !sessionId) return;

    let consecutivePositives = 0;
    let violationFired = false;

    const timer = setInterval(() => {
      const positive = isDevToolsLikelyOpen();

      if (!positive) {
        consecutivePositives = 0;
        // Reset once closed, so re-opening fires again.
        violationFired = false;
        return;
      }

      consecutivePositives += 1;
      if (consecutivePositives < 2 || violationFired) return;

      violationFired = true;
      logViolation("DevTools Opened During Exam");
      toast.error("Developer tools detected. This has been recorded.");
    }, 5_000);

    return () => {
      clearInterval(timer);
      clearFullscreenBaseline();
    };
  }, [phase, sessionId]);

  /**
   * Secondary monitor watcher for the active exam.
   *
   * A screen connected mid-exam is the same evasion as starting with one, so it is logged as its own
   * violation type — never as a DevTools event — and blocks the content until it is disconnected,
   * following the pattern the fullscreen modal already establishes.
   *
   * The violation fires at most once per attempt. Repeated logging for a monitor that stays plugged
   * in would drain the student's whole allowance for one continuous condition they are already being
   * blocked on, which is punishment without added information for the reviewer.
   */
  useEffect(() => {
    if (phase !== "active" || !sessionId) return;

    // Local to the effect rather than a state dependency: reading the flag from state would put it in
    // the dep array and tear the listener down on every toggle.
    let blocked = false;

    const evaluate = () => {
      const status = getMonitorStatus();

      if (!status.secondaryDetected) {
        if (blocked) {
          blocked = false;
          console.info("[Exam Gate] Secondary monitor no longer detected", status.detail);
          setSecondaryMonitorBlocked(false);
        }
        return;
      }

      blocked = true;
      setSecondaryMonitorBlocked(true);
      if (!secondaryMonitorViolationRef.current) {
        secondaryMonitorViolationRef.current = true;
        console.warn("[Exam Gate] Secondary monitor connected during exam", status);
        logViolation("Secondary Monitor Connected");
        toast.error("Secondary monitor detected. Disconnect external screens to continue.");
      }
    };

    // The Window Management API reports topology changes directly where it is supported; the interval
    // is the fallback for browsers that do not fire it.
    const unsubscribe = onDisplayChange(evaluate);
    const timer = setInterval(evaluate, 5_000);
    evaluate();

    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, [phase, sessionId]);

  /**
   * Heartbeat: tells the server the client is still here.
   *
   * Deliberately does NOT pause while the tab is hidden, unlike the staff-presence heartbeat this sits
   * beside in the codebase. That one skips hidden tabs to avoid counting idle time as work; here a
   * hidden tab is precisely the signal worth reporting, and going quiet is what the server's gap
   * detection is looking for. Pausing would let a student silence the proctor by backgrounding the tab —
   * the same action the tab-switch violation exists to catch.
   *
   * Browsers throttle timers in background tabs to roughly once a minute, which the server's 90-second
   * gap threshold is chosen to tolerate.
   */
  useEffect(() => {
    if (phase !== "active" || !sessionId) return;

    const intervalMs = Math.max(5, proctorConfig.heartbeatIntervalSeconds) * 1000;

    // Beat immediately so a resumed session is not silent for a whole interval — and so LastHeartbeatAt
    // is set at all, which is what distinguishes a live attempt from a legacy one the sweep must ignore.
    void sendExamHeartbeat(sessionId).then((res) => {
      if (res && !res.accepted && res.status && res.status !== "InProgress") {
        // The attempt ended somewhere else (force-submitted, terminated, expired server-side). Finding
        // out on a heartbeat rather than at submission time is the point of returning status here.
        setPhase(res.status === "Terminated" ? "terminated" : "selection");
        refetchPast();
      }
    });

    const timer = setInterval(() => {
      void sendExamHeartbeat(sessionIdRef.current).then((res) => {
        if (res && !res.accepted && res.status && res.status !== "InProgress") {
          setPhase(res.status === "Terminated" ? "terminated" : "selection");
          refetchPast();
        }
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [phase, sessionId, proctorConfig.heartbeatIntervalSeconds]);

  /**
   * Captures frames on the server's cadence for the length of the attempt.
   *
   * No longer opens a camera — it consumes the one the flow has held since the biometric gate. Failure
   * is no longer quiet either: a camera is now a hard requirement for the whole attempt, enforced by
   * the loss handling below, because "quiet failure" is precisely how attempts reached a reviewer with
   * an empty timeline and nothing to explain it.
   */
  useEffect(() => {
    if (phase !== "active" || !sessionId || !cameraReady) return;

    let cancelled = false;

    // A live stream exists a beat before the element has a decodable frame, and `captureFrame` correctly
    // refuses to encode that state. Waiting for real data is what keeps the SessionStart baseline — the
    // frame every later one gets compared against — from being the one frame that is always missing.
    const captureBaseline = async () => {
      const el = proctorVideoRef.current;
      if (el && el.readyState < 2) {
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, 3_000);
          el.addEventListener(
            "loadeddata",
            () => {
              clearTimeout(timer);
              resolve();
            },
            { once: true },
          );
        });
      }
      if (cancelled) return;
      void captureAndQueue(sessionIdRef.current || sessionId, proctorVideoRef.current, "SessionStart");
    };
    void captureBaseline();

    const intervalMs = Math.max(5, proctorConfig.snapshotIntervalSeconds) * 1000;
    const captureTimer = setInterval(() => {
      void captureAndQueue(sessionIdRef.current, proctorVideoRef.current, "Scheduled");
    }, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(captureTimer);
    };
  }, [phase, sessionId, cameraReady, proctorConfig.snapshotIntervalSeconds]);

  /**
   * Hard camera requirement for the length of the attempt.
   *
   * Opens a "loss episode" when the camera stops being usable mid-exam — unplugged, claimed by another
   * application, or revoked at the OS level. The episode blocks the questions and runs a grace countdown
   * (see the overlay in the active-phase JSX); the countdown effect below auto-retries and ends the
   * attempt if it expires.
   *
   * `acquiring` is deliberately not treated as a loss: the owner's own retry backoff is ~3s, and
   * flashing a blocking dialog at a device that is about to come back would be worse than the wait.
   */
  const cameraLossActive = cameraGraceLeft !== null;
  const cameraLossEpisodeRef = useRef(false);

  useEffect(() => {
    if (phase !== "active" || !sessionId) {
      cameraLossEpisodeRef.current = false;
      cameraTerminationRef.current = false;
      setCameraGraceLeft(null);
      return;
    }

    // Only an explicit failure counts as a loss.
    //
    // "acquiring" is a device that is about to come back — the owner's own backoff is ~3s, and
    // flashing a blocking dialog through it would be worse than the wait. "idle" means nothing has
    // been asked for yet, which is the state a resume lands in for exactly one render: the acquire
    // effect above runs first and flips it, but this effect's closure still reads the pre-acquire
    // value. Treating that render as a loss would open a spurious episode — and terminate the attempt
    // of a student who did nothing but refresh the page.
    const cameraFailed = camera.status === "error" || camera.status === "lost";
    if (!cameraReady && !cameraFailed) return;

    if (cameraReady) {
      if (cameraLossEpisodeRef.current) {
        cameraLossEpisodeRef.current = false;
        setCameraGraceLeft(null);
        toast.success("Camera restored — monitoring has resumed.");
        // Audited so a reviewer reading the gap in the frame timeline can tell a recovered
        // interruption from an attempt that simply stopped sending. Without the pair, both look
        // identical after the fact.
        void sendDiagnosticEvent(
          sessionIdRef.current || sessionId,
          "CameraRestored",
          "Webcam recovered within the grace window; frame capture resumed.",
        );
      }
      return;
    }

    if (cameraLossEpisodeRef.current) return;
    cameraLossEpisodeRef.current = true;
    cameraTerminationRef.current = false;
    setCameraGraceLeft(proctorConfig.cameraGraceSeconds);

    // "Lost" means it worked and then went away; anything else means it never came up on this attempt.
    // The distinction is the whole value of the audit row — one is a hardware event mid-exam, the other
    // is a session that was never monitored.
    void sendDiagnosticEvent(
      sessionIdRef.current || sessionId,
      camera.status === "lost" ? "CameraLost" : "CameraUnavailable",
      `Webcam unusable during active attempt (${camera.error?.kind ?? camera.status}): ` +
        `${camera.error?.message ?? "no further detail"}`,
    );
  }, [
    phase,
    sessionId,
    camera.status,
    camera.error,
    cameraReady,
    proctorConfig.cameraGraceSeconds,
  ]);

  /**
   * Grace countdown: ticks once a second, retries the device as it goes, and terminates the attempt if
   * the window closes with no camera.
   *
   * Terminating is what makes "camera required" mean something. Leaving the student blocked until their
   * time simply ran out would produce the same zero score with no record of why.
   */
  useEffect(() => {
    if (phase !== "active" || !cameraLossActive) return;

    if ((cameraGraceLeft ?? 0) <= 0) {
      if (cameraTerminationRef.current) return;
      cameraTerminationRef.current = true;
      void terminateDueToCameraLoss();
      return;
    }

    const tick = setTimeout(() => {
      setCameraGraceLeft((n) => (n === null ? null : n - 1));
    }, 1_000);
    return () => clearTimeout(tick);
  }, [phase, cameraLossActive, cameraGraceLeft]);

  // Auto-retry while the episode is open. Keyed on `cameraLossActive` rather than the countdown value
  // so the interval is created once per episode instead of being torn down and rebuilt every tick.
  useEffect(() => {
    if (phase !== "active" || !cameraLossActive) return;
    const retry = setInterval(() => {
      void retryCamera();
    }, 3_000);
    return () => clearInterval(retry);
  }, [phase, cameraLossActive]);

  /**
   * Last-gasp delivery when the page is going away.
   *
   * `pagehide`/`visibilitychange` are the only events that reliably fire on a real tab close or a mobile
   * app switch — `beforeunload` does not on mobile Safari. Nothing can be awaited here, so this is a
   * best-effort kick of the queue; the durable record in localStorage is what actually guarantees the
   * violation survives, and `resumeViolationQueue` above is what delivers it on the next page.
   */
  useEffect(() => {
    if (phase !== "active" || !sessionId) return;

    const kickQueues = () => {
      const active = sessionIdRef.current;
      if (!active) return;
      void flushViolations(active, 1_500);
    };

    window.addEventListener("pagehide", kickQueues);
    return () => window.removeEventListener("pagehide", kickQueues);
  }, [phase, sessionId]);

  // Release pending frames when the attempt is over, so a finished exam holds no image blobs.
  useEffect(() => {
    if (phase === "result" || phase === "selection") {
      const finished = sessionIdRef.current;
      if (finished) clearSnapshotQueue(finished);
      pendingViolationFrames.current = {};
    }
  }, [phase]);

  // Synchronize active URL query parameter with active phase to prevent getting stuck
  useEffect(() => {
    const search = location.search as any;
    const hasActiveParam = search?.active === "true" || search?.active === true;

    if (phase === "active" && !hasActiveParam) {
      navigate({ search: { active: true } });
    } else if (phase !== "active" && hasActiveParam) {
      navigate({ search: {} });
    }
  }, [phase, location.search]);

  // Global Exam timer countdown ticker — pauses when offline via CustomEvent
  useEffect(() => {
    if (phase !== "active" || examTimeRemaining <= 0) return;

    let paused = false;

    const onPause = () => { paused = true; };
    const onResume = () => { paused = false; };
    window.addEventListener("exam:offline-pause", onPause);
    window.addEventListener("exam:online-resume", onResume);

    const timer = setInterval(() => {
      if (paused) return;
      setExamTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          autoSubmitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      window.removeEventListener("exam:offline-pause", onPause);
      window.removeEventListener("exam:online-resume", onResume);
    };
  }, [phase, examTimeRemaining]);

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // Persist any in-flight (debounced) essay/short-answer text before an emergency lock so
  // the last keystrokes aren't lost. Reads refs (not closures) for the true latest values.
  //
  // allSettled, not all: one failed save must not abandon the others. A rejected persist used to reject
  // the whole flush, so a single dead request discarded every other pending answer along with it.
  const flushPendingAnswers = async () => {
    const pendingIds = Object.keys(essaySaveTimers.current);
    if (pendingIds.length === 0) return;
    await Promise.allSettled(
      pendingIds.map((qid) => {
        clearTimeout(essaySaveTimers.current[qid]);
        delete essaySaveTimers.current[qid];
        const val = textAnswersRef.current[qid] ?? "";
        const q = questionsRef.current.find((qq) => qq.id === qid);
        const isEssay = q?.question_type === "essay" || (q as any)?.question_type === "Essay";
        return isEssay ? persistEssay(qid, val) : persistShortAnswer(qid, val);
      }),
    );
  };

  /**
   * Records a proctoring violation.
   *
   * Two things happen synchronously before any network call, and both matter:
   *
   * 1. The violation is written to the durable queue, so it survives a dropped connection, a reload, or
   *    the student closing the laptop in the same second. This replaced a bare fire-and-forget POST
   *    whose only failure handling was `console.error` — a violation lost to a Wi-Fi blip was lost
   *    permanently, which made the network the easiest way to defeat the proctor.
   * 2. A webcam frame is captured at this instant. Not a second later from a timer — the point of a
   *    violation-triggered frame is to show who was in front of the machine at the moment it happened.
   *
   * The frame is held until the queue reports the server's violation id, then uploaded attached to it.
   * If that acknowledgement does not arrive in time, it is uploaded unattached rather than dropped: a
   * violation-flagged frame with a timestamp is still the evidence a reviewer needs.
   */
  const logViolation = (type: string) => {
    const activeSession = sessionIdRef.current || sessionId;
    if (!activeSession) return;

    // Derived from the ref and committed before anything else, so this function is safe to call from an
    // event handler and impossible to replay. Nothing below runs inside a React updater.
    const nextCount = violationCountRef.current + 1;
    setViolationCount(nextCount);

    const allowed = selectedExam?.max_violations ?? 3;
    const eventId = enqueueViolation(activeSession, `${type} (Violation #${nextCount})`);

    // Capture is async (canvas encode). Held by event id and attached to the server's violation row in
    // the ack subscription above.
    void captureFrame(proctorVideoRef.current).then((blob) => {
      if (!blob) return;
      pendingViolationFrames.current[eventId] = blob;
      // Safety net: if no ack lands within the window a healthy connection would need many times over,
      // upload unattached so the frame is not held hostage to the violation's delivery.
      setTimeout(() => {
        const held = pendingViolationFrames.current[eventId];
        if (!held) return;
        delete pendingViolationFrames.current[eventId];
        queueSnapshot(activeSession, held, "Violation", null);
      }, 15_000);
    });

    // The local threshold check stays: it locks the UI immediately instead of leaving a student who has
    // already breached the limit free to keep reading questions while a POST is in flight. The server
    // enforces the same ceiling independently and its verdict wins (see the ack handler).
    if (nextCount >= allowed) {
      void terminateDueToViolations();
      return;
    }

    setShowViolationWarning(true);
    toast.warning(`Security warning: ${type} detected. (${nextCount}/${allowed} violations)`);
  };

  /**
   * Emergency termination when the violation threshold is hit: lock the UI first so input stops, then
   * make sure the server has everything before the session is closed out.
   *
   * Order is deliberate. The UI locks synchronously, because anything awaited first is time a student
   * who has already breached the limit spends still reading questions. Then answers and the violation
   * queue are flushed — the violation that triggered this is itself still in that queue, and it is the
   * one that makes the termination defensible after the fact.
   *
   * `terminateExamSession` is called unconditionally rather than relying on the final violation POST to
   * terminate server-side. The previous version only fell back to it if that POST rejected, so a
   * violation still sitting in a retry backoff left the session InProgress on the server while the
   * student saw a locked screen — a state the expiry sweep would eventually resolve as an abandoned
   * attempt, minutes later and for the wrong reason.
   */
  const terminateDueToViolations = async () => {
    const activeSession = sessionIdRef.current || sessionId;
    setPhase("terminated"); // Lock UI IMMEDIATELY
    toast.error(
      "Exam locked: proctoring violation limit reached. Your answers have been submitted.",
    );
    try {
      await flushPendingAnswers();
    } catch (e) {
      console.error("[Flush before lock failed]", e);
    }

    if (activeSession) {
      // Concurrent, and with a longer budget than a normal submit: the UI is already locked so nobody is
      // waiting on a result screen, and this is the last chance to deliver the violations that justify
      // locking the attempt. Still bounded — an unbounded wait here would leave a student staring at a
      // locked screen with no resolution.
      await Promise.race([
        Promise.allSettled([
          flushViolations(activeSession, 8_000),
          flushSnapshots(activeSession, 8_000),
        ]),
        new Promise((resolve) => setTimeout(resolve, 8_000)),
      ]);
      await terminateExamSession(activeSession).catch(console.error);
    }

    refetchPast();
    // Exam state changed (session consumed) — refresh the exam cards so the student sees
    // the correct locked/available state immediately, not after the 5-min staleTime.
    qc.invalidateQueries({ queryKey: ["available-exams", user?.id] });
  };

  /**
   * Ends the attempt when the grace window closes with no camera.
   *
   * Modelled on `terminateDueToViolations`, with one deliberate difference: this is NOT a violation. The
   * student did not necessarily do anything — a webcam can genuinely die mid-exam — so nothing is
   * counted against `MaxViolations` and the message says what happened rather than accusing them. The
   * termination itself is the consequence, and the `CameraLost` audit row already written when the
   * episode opened is what explains it to whoever reviews the attempt.
   *
   * Answers are flushed first, exactly as in the violation path: the work the student actually did
   * before their camera failed should still reach the server.
   */
  const terminateDueToCameraLoss = async () => {
    const activeSession = sessionIdRef.current || sessionId;
    setPhase("terminated"); // Lock UI IMMEDIATELY
    setCameraGraceLeft(null);
    toast.error(
      "Exam ended: webcam monitoring is required and the camera could not be restored in time. " +
        "Your answers have been submitted. Contact your instructor to request another attempt.",
      { duration: 15_000 },
    );

    try {
      await flushPendingAnswers();
    } catch (e) {
      console.error("[Flush before camera-loss lock failed]", e);
    }

    if (activeSession) {
      void sendDiagnosticEvent(
        activeSession,
        "CameraLost",
        `Grace window of ${proctorConfig.cameraGraceSeconds}s expired with no usable camera; ` +
          "attempt terminated by the client.",
      );

      // Bounded, like the violation path: the UI is already locked, so nothing is waiting on this, but
      // an unbounded wait would leave the student on a dead screen with no resolution.
      await Promise.race([
        Promise.allSettled([
          flushViolations(activeSession, 8_000),
          flushSnapshots(activeSession, 8_000),
        ]),
        new Promise((resolve) => setTimeout(resolve, 8_000)),
      ]);
      await terminateExamSession(activeSession).catch(console.error);
    }

    refetchPast();
    qc.invalidateQueries({ queryKey: ["available-exams", user?.id] });
  };

  /**
   * Gets everything the server still needs to know about this attempt to the server, before the attempt
   * is closed.
   *
   * This closes the submission race. Both submit paths previously called `completeExamSession` directly,
   * and `flushPendingAnswers` — which only covers debounced answer saves — was wired into the
   * termination path alone. A violation raised in the seconds before submitting had its POST abandoned
   * mid-flight when the component tore down, so the most incriminating violations (the ones immediately
   * before a suspiciously fast submit) were the likeliest of all to be lost.
   *
   * Bounded on purpose: a student on a broken connection must still be able to submit a finished exam.
   * The server-side heartbeat gap flag covers what a timeout here leaves behind, since a client that
   * cannot flush a violation was not sending heartbeats either.
   */
  const flushProctoringBeforeSubmit = async () => {
    const activeSession = sessionIdRef.current || sessionId;

    // Webcam frames are kicked and DELIBERATELY NOT AWAITED. They are the least important artefact on
    // this path and the most expensive to wait for — tens of kilobytes each — and a student must never
    // find their finished exam held up, or failed, because an image upload was slow. The queue keeps
    // working on them after this returns; if the page dies first the frames are lost, which costs some
    // context in a review and changes no finding.
    if (activeSession) void flushSnapshots(activeSession, 15_000);

    // Answers and violations run CONCURRENTLY under ONE shared deadline, not one after another. Chaining
    // them made the worst case the sum of both waits on top of an unbounded answer flush, so a weak
    // connection could stall submission for as long as it took every request to individually time out.
    // They are independent writes, so there is no reason to serialise them.
    const flushes: Promise<unknown>[] = [
      flushPendingAnswers().catch((e) => {
        console.error("[Flush answers before submit failed]", e);
      }),
    ];
    if (activeSession) flushes.push(flushViolations(activeSession, PRE_SUBMIT_FLUSH_BUDGET_MS));

    // allSettled, so one failing branch cannot reject the other; raced against a single hard ceiling so
    // the total wait is bounded no matter how the individual requests behave. Whatever has not landed by
    // then keeps going in the background — the requests are already in flight and the violation queue is
    // durable — while the student proceeds to their result.
    await Promise.race([
      Promise.allSettled(flushes),
      new Promise((resolve) => setTimeout(resolve, PRE_SUBMIT_FLUSH_BUDGET_MS)),
    ]);
  };

  // Auto-submit when timer hits zero
  const autoSubmitExam = async () => {
    setSubmitting(true);
    toast.info("Exam time is up! Automatically submitting your answers...");
    try {
      await flushProctoringBeforeSubmit();
      const res = await completeExamSession(sessionId);
      clearExamDraft(sessionId);
      setResult(res);
      setPhase("result");
      navigate({ search: {} });
      refetchPast();
      await invalidateAllStudentState(qc, user?.id);

      // Poll for auto-issued certificate from DB triggers
      if (res.passed) {
        setTimeout(async () => {
          try {
            const newCerts = await fetchRecentlyIssuedCertificates(15);
            if (newCerts.length > 0) {
              toast.success("🎓 Certificate issued! Check your Certificates page.", {
                duration: 8000,
              });
              qc.invalidateQueries({ queryKey: ["user-certificates", user?.id] });
            }
          } catch (e) {
            console.warn("[cert poll]", e);
          }
        }, 2500);
      }
    } catch (err: any) {
      toast.error(`Auto-submit failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Capture a frame from the video stream to a Blob. Resolves the promise with the blob (or null),
  // so callers can await the real result instead of racing a fixed timer.
  const captureSelfie = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const video = videoRef.current;
      if (!video) return resolve(null);
      // A live MediaStream exists a beat before the first frame is decodable. Drawing during that
      // window yields an all-black JPEG — which would then be stored as the student's identity
      // evidence for the sitting. Returning null instead surfaces the retryable "Could not capture
      // a clear photo" path in triggerSelfieScan.
      if (video.readyState < 2 /* HAVE_CURRENT_DATA */ || !video.videoWidth) return resolve(null);
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 640;
        canvas.height = 480;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);
        // Mirror the canvas context since the video preview is mirrored scale-x-[-1]
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
      } catch (e) {
        console.error("Failed to capture webcam selfie:", e);
        resolve(null);
      }
    });
  };

  // Capture → upload → verify, fully sequenced. The status only becomes "verified" AFTER the upload
  // has genuinely succeeded, so the UI can never show a green check while the selfie/URL are unset.
  // On any failure we land in "error" (visible + retryable) instead of silently bouncing the phase.
  const triggerSelfieScan = async () => {
    // "Still warming up" is not the same failure as "no camera", and telling a student to enable a
    // camera that is visibly starting is what made a slow camera look like a broken one.
    if (cameraStarting) {
      toast.info("The camera is still starting. Give it a moment and try again.");
      return;
    }
    if (!cameraReady) {
      // The owner's message already names the actual cause (denied / busy / insecure origin), so it is
      // more useful here than a generic instruction to enable a camera that may already be enabled.
      toast.error(
        camera.error?.message ??
          "Camera access is required to capture your verification selfie. Enable your camera and try again.",
      );
      return;
    }
    setBiometricError(null);
    setBiometricStatus("capturing");

    // Short delay is purely a "scanning" affordance; the outcome is driven by the real async result.
    await new Promise((r) => setTimeout(r, 1000));
    const blob = await captureSelfie();
    if (!blob) {
      setBiometricStatus("error");
      setBiometricError("Could not capture a clear photo. Please try again.");
      return;
    }
    setSelfieBlob(blob);
    setBiometricStatus("uploading");

    const uploaded = await uploadBiometricSelfie(blob);
    if (!uploaded.ok) {
      setBiometricStatus("error");
      setBiometricError(uploaded.message);
      toast.error(uploaded.message);
      return;
    }

    setSelfieUrl(uploaded.url);
    setBiometricStatus("verified");
    if (selectedExam) verifiedForExamRef.current = selectedExam.id;
    toast.success("Selfie scanning verification completed.");
  };

  // API Call: Start or Resume exam session and load questions
  const initializeExamSession = async (existingSessionId?: string | null) => {
    if (!selectedExam || !user) return;
    setLoading(true);
    try {
      // ── Device gate ──────────────────────────────────────────────────────
      // Block mobile / tablet before any session is created. The check is cheap and
      // synchronous — no reason to hit the server for a device that will be rejected.
      const device = checkDeviceEligibility();
      if (!device.allowed) {
        setDeviceBlocked(true);
        setDeviceBlockReason(device.reason);
        toast.error(device.reason ?? "This device cannot take exams.");
        return;
      }

      // ── Secondary monitor gate ───────────────────────────────────────────
      // Ordered before the DevTools probe on purpose. An extra display changes the measurements the
      // DevTools heuristic reads, so checking it second meant a student with a second screen was
      // shown "close developer tools" — an instruction they could not act on, for a condition that
      // was not theirs. Whichever check is genuinely responsible must be the one that speaks.
      const monitors = getMonitorStatus();
      if (monitors.secondaryDetected) {
        console.warn("[Exam Gate] Blocked by secondary monitor", monitors);
        setSecondaryMonitorBlocked(true);
        toast.error("Please disconnect all external screens before taking the exam.");
        return;
      }

      // ── DevTools gate ────────────────────────────────────────────────────
      // Full probe (fullscreen shortfall + debugger timing) runs once at exam start. If positive,
      // the student must close DevTools before they can proceed — the phase never transitions.
      const devToolsOpen = await probeDevTools();
      if (devToolsOpen) {
        setDevToolsBlocked(true);
        logViolation("DevTools Detected at Start");
        toast.error("Please close developer tools before starting the exam.");
        return;
      }

      // ── Camera gate (independent of biometric) ──────────────────────────
      // Webcam monitoring is a hard requirement for the whole attempt, so a session is never created
      // without a live camera. This gate runs for ALL exams — biometric or not.
      //
      // It now tests the stream the attempt will actually monitor with, which the flow is already
      // holding open. The old version probed a throwaway stream and closed it, so passing this gate
      // said nothing about whether the monitoring stream would open a moment later — and on Windows,
      // where closing the probe left the device still releasing, passing it actively made the real
      // acquisition fail.
      //
      // The refusal applies to a FRESH START only. On a resume the attempt's clock is already
      // running, so turning the student away spends their exam time on a door they cannot open —
      // one real session lost 47 of its 90 minutes to exactly this, then auto-submitted at expiry.
      // Blocking is a defensible answer when the cost is "you don't start yet"; it is not one when
      // the cost is the attempt itself.
      //
      // So a resume is admitted with a warning and an audit row, and the mid-exam loss overlay takes
      // over from there: it retries the device, and ends the attempt if the grace window expires. The
      // camera is still mandatory — the student just gets to spend the grace window fixing it instead
      // of watching a disabled button.
      if (!cameraReady) {
        if (!existingSessionId) {
          toast.error(
            camera.error?.message ??
              "Camera access is required for this proctored exam. Please enable your camera and try again.",
            { duration: 8000 },
          );
          return;
        }

        toast.warning(
          `${camera.error?.message ?? "Your camera is not available."} Your exam is already running, ` +
            `so you are being let back in — but monitoring must resume within ` +
            `${proctorConfig.cameraGraceSeconds} seconds or the attempt will end.`,
          { duration: 10000 },
        );
        void sendDiagnosticEvent(
          existingSessionId,
          "CameraUnavailable",
          `Resumed an in-progress attempt with no usable webcam (${camera.error?.kind ?? camera.status}): ` +
            `${camera.error?.message ?? "no further detail"}. Admitted under the grace window rather ` +
            "than blocked, because the attempt clock was already running.",
        );
      }

      // Mandatory biometric gate — runs for BOTH fresh starts AND reopened resumes. A normal
      // same-sitting resume (page refresh) arrives with biometricStatus already set to "verified"
      // from the button handler and will not be caught here. A reopened session arrives after the
      // full biometric/preflight sequence, so it must also pass this gate.
      if ((selectedExam as any).is_biometric_required) {
        // Camera unavailability is no longer one of this machine's states — the gate above already
        // refused on it, with a message about the actual device problem rather than about biometrics.
        if (biometricStatus === "error") {
          toast.error(
            biometricError ?? "Biometric verification failed. Please retry your photo.",
          );
          // Stay on the current phase; the inline "Retake Photo" affordance is the only way back
          // into the camera, so we don't re-open it and repeat the same failure.
          return;
        }
        if (biometricStatus !== "verified" || !selfieUrl) {
          toast.error("A verification selfie is required before starting this exam.");
          setPhase("biometric");
          return;
        }
      }

      let sessId = existingSessionId;

      if (!sessId) {
        sessId = await createExamSession(
          user.id,
          selectedExam.id,
          biometricStatus === "verified",
          selfieUrl,
        );
      }

      setSessionId(sessId);
      const loadedQuestions = await fetchSessionQuestions(sessId);

      // Guard: abort if exam has no questions (misconfigured exam)
      if (!loadedQuestions || loadedQuestions.length === 0) {
        if (!existingSessionId) {
          await terminateExamSession(sessId).catch(console.error);
        }
        throw new Error(
          "This exam has no questions configured. Please contact your administrator.",
        );
      }

      const parseOptions = (val: any) => {
        if (Array.isArray(val)) return val;
        try {
          return JSON.parse(val || "[]");
        } catch (e) {
          console.error("Error parsing question options:", e);
          return [];
        }
      };

      // Backend returns camelCase fields and PascalCase question types
      // (MultipleChoice/MultiSelect/Essay); normalise to the UI's snake_case shape.
      const typeMap: Record<string, string> = {
        MultipleChoice: "mcq",
        MultiSelect: "multi_select",
        Essay: "essay",
        TrueFalse: "true_false",
        ShortAnswer: "short_answer",
      };
      const parsedQuestions = loadedQuestions.map((q: any) => ({
        id: q.id ?? q.Id,
        question_text: q.questionText ?? q.question_text ?? q.QuestionText ?? "",
        question_type: typeMap[q.questionType ?? q.question_type] ?? "mcq",
        category: q.category ?? q.Category ?? null,
        points: q.points ?? q.Points ?? 1,
        time_limit_seconds: q.timeLimitSeconds ?? q.time_limit_seconds,
        options: parseOptions(q.options ?? q.Options),
      }));

      setQuestions(parsedQuestions);
      setCurrentQuestionIndex(0);

      if (existingSessionId) {
        // Resume: rehydrate saved answers, remaining time, and violation count from the
        // server rather than starting blank with a fresh countdown.
        const state = await fetchSessionState(sessId).catch(() => null);
        const restoredSelected: Record<string, string> = {};
        const restoredText: Record<string, string> = {};

        for (const r of state?.responses ?? []) {
          const q = parsedQuestions.find((pq) => pq.id === r.question_id);
          if (!q) continue;

          if (q.question_type === "essay") {
            restoredText[q.id] = r.selected_answer ?? "";
            restoredSelected[q.id] = (r.selected_answer ?? "").trim() ? "selected" : "";
          } else if (q.question_type === "short_answer") {
            // Free text auto-graded — store the raw answer.
            restoredText[q.id] = r.selected_answer ?? "";
            restoredSelected[q.id] = (r.selected_answer ?? "").trim() ? "selected" : "";
          } else if (q.question_type === "multi_select") {
            let texts: string[] = [];
            try {
              texts = JSON.parse(r.selected_answer || "[]");
            } catch {
              texts = [];
            }
            const letters = texts
              .map((t) => q.options.indexOf(t))
              .filter((i: number) => i >= 0)
              .map((i: number) => String.fromCharCode(65 + i))
              .sort();
            const joined = letters.join(",");
            restoredText[q.id] = joined;
            restoredSelected[q.id] = joined ? "selected" : "";
          } else {
            // MCQ — map the stored option text back to its letter for UI highlighting.
            const idx = q.options.indexOf(r.selected_answer);
            if (idx >= 0) restoredSelected[q.id] = String.fromCharCode(65 + idx);
          }
        }

        setSelectedAnswers(restoredSelected);
        setTextAnswers(restoredText);
        setViolationCount(state?.violation_count ?? 0);
        setExamTimeRemaining(
          state && state.seconds_remaining > 0
            ? state.seconds_remaining
            : selectedExam.time_limit_minutes * 60,
        );
      } else {
        setSelectedAnswers({});
        setTextAnswers({});
        setViolationCount(0);
        setExamTimeRemaining(selectedExam.time_limit_minutes * 60);
      }

      setReopenedSessionId(null);
      setPhase("active");
      navigate({ search: { active: true } });
      toast.success(
        existingSessionId
          ? "Exam resumed successfully. Keep full screen focus!"
          : "Exam started successfully. Keep full screen focus!",
      );
    } catch (err: any) {
      toast.error(`Could not start exam: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // API Call: Save answer response on selection
  const handleAnswerSelect = async (optionIndex: number) => {
    if (!sessionId || !questions.length) return;
    const currentQ = questions[currentQuestionIndex];
    const answerLetter = String.fromCharCode(65 + optionIndex);

    // Update local state
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQ.id]: answerLetter,
    }));

    // Save response asynchronously. The backend grades against the option TEXT,
    // so send the option text (the letter is only used for UI highlighting).
    try {
      await saveExamResponse({
        sessionId,
        questionId: currentQ.id,
        selectedAnswer: currentQ.options[optionIndex],
        timeTakenSeconds: 0,
      });
    } catch (err) {
      console.error("[Save Answer Fail]", err);
      toast.error("Failed to sync answer with the server. Trying again...");
    }
  };

  const handleMultiSelect = async (optionIndex: number) => {
    if (!sessionId || !questions.length) return;
    const currentQ = questions[currentQuestionIndex];
    const answerLetter = String.fromCharCode(65 + optionIndex);

    let currentLetters = textAnswers[currentQ.id] ? textAnswers[currentQ.id].split(",") : [];
    if (currentLetters.includes(answerLetter)) {
      currentLetters = currentLetters.filter((l) => l !== answerLetter);
    } else {
      currentLetters.push(answerLetter);
    }
    currentLetters.sort();
    const joined = currentLetters.join(",");

    setTextAnswers((prev) => ({ ...prev, [currentQ.id]: joined }));
    setSelectedAnswers((prev) => ({ ...prev, [currentQ.id]: joined ? "selected" : "" }));

    // Map selected letters back to option texts (backend does exact-set matching on text).
    const selectedTexts = currentLetters
      .map((l) => currentQ.options[l.charCodeAt(0) - 65])
      .filter((t): t is string => typeof t === "string");

    try {
      await saveExamResponse({
        sessionId,
        questionId: currentQ.id,
        selectedAnswers: selectedTexts,
        timeTakenSeconds: 0,
      });
    } catch (err) {
      console.error("[Save Answer Fail]", err);
    }
  };

  const persistEssay = async (questionId: string, val: string) => {
    if (!sessionId) return;
    try {
      await saveEssayResponse({ sessionId, questionId, textAnswer: val });
    } catch (err) {
      console.error("[Save Essay Fail]", err);
    }
  };

  const handleEssayChange = async (questionId: string, val: string) => {
    setTextAnswers((prev) => ({ ...prev, [questionId]: val }));
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: val.trim() ? "selected" : "" }));

    // Debounced autosave: persist ~1.2s after typing stops so an answer survives a
    // crash/timeout/tab-close even if the field is never blurred.
    if (essaySaveTimers.current[questionId]) {
      clearTimeout(essaySaveTimers.current[questionId]);
    }
    essaySaveTimers.current[questionId] = setTimeout(() => {
      delete essaySaveTimers.current[questionId];
      persistEssay(questionId, val);
    }, 1200);
  };

  const handleEssayBlur = async (questionId: string) => {
    // Blur saves immediately — cancel any pending debounce first to avoid a double save.
    if (essaySaveTimers.current[questionId]) {
      clearTimeout(essaySaveTimers.current[questionId]);
      delete essaySaveTimers.current[questionId];
    }
    await persistEssay(questionId, textAnswers[questionId] || "");
  };

  // Short-answer: free text that is auto-graded by exact (case-insensitive) match. Reuses the
  // essay debounce pattern but saves through the auto-graded response endpoint.
  const persistShortAnswer = async (questionId: string, val: string) => {
    if (!sessionId) return;
    try {
      await saveExamResponse({ sessionId, questionId, selectedAnswer: val, timeTakenSeconds: 0 });
    } catch (err) {
      console.error("[Save Short Answer Fail]", err);
    }
  };

  const handleShortAnswerChange = (questionId: string, val: string) => {
    setTextAnswers((prev) => ({ ...prev, [questionId]: val }));
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: val.trim() ? "selected" : "" }));
    if (essaySaveTimers.current[questionId]) clearTimeout(essaySaveTimers.current[questionId]);
    essaySaveTimers.current[questionId] = setTimeout(() => {
      delete essaySaveTimers.current[questionId];
      persistShortAnswer(questionId, val);
    }, 1200);
  };

  const handleShortAnswerBlur = async (questionId: string) => {
    if (essaySaveTimers.current[questionId]) {
      clearTimeout(essaySaveTimers.current[questionId]);
      delete essaySaveTimers.current[questionId];
    }
    await persistShortAnswer(questionId, textAnswers[questionId] || "");
  };

  const submitExam = async () => {
    setSubmitting(true);
    try {
      await flushProctoringBeforeSubmit();
      const res = await completeExamSession(sessionId);
      clearExamDraft(sessionId);
      setResult(res);
      setPhase("result");
      navigate({ search: {} });
      refetchPast();
      await invalidateAllStudentState(qc, user?.id);
      toast.success("Exam submitted successfully!");

      // Poll for auto-issued certificate from DB triggers (course/track cert)
      if (res.passed) {
        setTimeout(async () => {
          try {
            const newCerts = await fetchRecentlyIssuedCertificates(15);
            if (newCerts.length > 0) {
              toast.success("🎓 Certificate issued! Check your Certificates page.", {
                duration: 8000,
              });
              qc.invalidateQueries({ queryKey: ["user-certificates", user?.id] });
            }
          } catch (e) {
            console.warn("[cert poll]", e);
          }
        }, 2500);
      }
    } catch (err: any) {
      toast.error(`Submission error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper formatting for seconds to MM:SS
  const formatSeconds = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // ── Selection-view search & filtering ─────────────────────────────────
  // Grouping of available exams into the categorized dashboard sections. A checkpoint exam is
  // identified by its gating checkpoint so it always lands in the Checkpoint section (even when
  // locked) rather than getting lost among assigned exams.
  const categorizeExam = (e: any): "checkpoint" | "course_final" | "track_final" | "assigned" => {
    if (e.is_checkpoint || e.checkpoint_id) return "checkpoint";
    const kind = String(e.exam_type ?? "");
    if (kind === "CourseFinal") return "course_final";
    if (kind === "TrackFinal") return "track_final";
    if (kind === "CheckpointExam") return "checkpoint";
    return "assigned";
  };
  const examSectionDefs = [
    { key: "checkpoint", title: "Checkpoint Exams" },
    { key: "assigned", title: "Assigned Exams" },
    { key: "course_final", title: "Course Final Exams" },
    { key: "track_final", title: "Track Final Exams" },
  ] as const;

  // One status per exam, resolved in the SAME precedence order the card badge uses, so what the
  // student filters by is always what the card in front of them says.
  const examStatusKey = (
    e: any,
  ): "in_progress" | "pending_grading" | "passed" | "failed" | "locked" | "available" => {
    if (e.has_active_session) return "in_progress";
    if (e.exam_status === "pending_grading") return "pending_grading";
    if (e.already_passed) return "passed";
    if (e.is_completed) return "failed";
    if (e.is_locked) return "locked";
    return "available";
  };
  const examStatusFilterDefs = [
    { key: "all", label: "All Statuses" },
    { key: "available", label: "Ready to Start" },
    { key: "in_progress", label: "In Progress" },
    { key: "pending_grading", label: "Pending Grading" },
    { key: "passed", label: "Passed" },
    { key: "failed", label: "Failed" },
    { key: "locked", label: "Locked" },
  ] as const;

  const normalizedExamSearch = examSearch.trim().toLowerCase();
  const hasExamFilters =
    normalizedExamSearch.length > 0 || examStatusFilter !== "all" || examTypeFilter !== "all";

  const filteredExams = useMemo(() => {
    return availableExams.filter((e: any) => {
      if (examTypeFilter !== "all" && categorizeExam(e) !== examTypeFilter) return false;
      if (examStatusFilter !== "all" && examStatusKey(e) !== examStatusFilter) return false;
      if (!normalizedExamSearch) return true;
      return [e.title, e.description, e.course_title, e.checkpoint_title].some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(normalizedExamSearch),
      );
    });
  }, [availableExams, normalizedExamSearch, examStatusFilter, examTypeFilter]);

  const clearExamFilters = () => {
    setExamSearch("");
    setExamStatusFilter("all");
    setExamTypeFilter("all");
  };

  // Render loaders
  if (isLoadingExams || isLoadingPast || (!isAdmin && isLoadingAvailable)) {
    return (
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <CardSkeleton count={6} className="md:grid-cols-2 lg:grid-cols-3" />
      </div>
    );
  }

  const activeQuestion =
    phase === "active" && questions.length > 0 ? questions[currentQuestionIndex] : null;

  const examSections = examSectionDefs
    .map((def) => ({ ...def, exams: filteredExams.filter((e) => categorizeExam(e) === def.key) }))
    .filter((s) => s.exams.length > 0);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* PHASE 1: EXAM SELECTION VIEW */}
      {phase === "selection" && (
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-primary" />
              Exam Center
            </h1>
            <p className="text-muted-foreground mt-2">
              Complete assigned exams and check your certification eligibility status.
            </p>
          </div>

          {!isTrainingComplete &&
            (hasRemedialLock ? (
              <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-900/50 rounded-xl text-sm">
                <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-500" />
                <div>
                  <span className="font-semibold">Remedial Review Required:</span> You did not pass
                  a checkpoint exam. Some modules have been locked and must be re-watched before you
                  can retake the exam. Go to the{" "}
                  <Link
                    to="/training"
                    className="font-bold underline hover:text-red-900 dark:hover:text-red-200"
                  >
                    Training Modules
                  </Link>{" "}
                  tab to complete the remedial review.
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900/50 rounded-xl text-sm">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-500" />
                <div>
                  <span className="font-semibold">Training Incomplete:</span> You must complete all
                  training modules before you can take the exams. Go to the{" "}
                  <Link
                    to="/training"
                    className="font-bold underline hover:text-amber-900 dark:hover:text-amber-200"
                  >
                    Training Modules
                  </Link>{" "}
                  tab to complete them.
                </div>
              </div>
            ))}

          {isMobilePhoneUA() && !isAdmin && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-900/50 rounded-xl text-sm">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-500" />
              <div>
                <span className="font-semibold">Mobile phones are not allowed:</span> Exams must be
                taken from a laptop, desktop, or tablet. Please open this page from a computer to
                start the exam.
              </div>
            </div>
          )}

          {availableExams.length === 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card className="col-span-full py-16 text-center space-y-4">
                <ShieldCheck className="h-16 w-16 mx-auto text-muted-foreground/30" />
                <div>
                  <h3 className="text-lg font-semibold">No Active Exams</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    There are no exams assigned to you at the moment.
                  </p>
                </div>
              </Card>
            </div>
          ) : (
            <div className="space-y-10">
              {/* Search + filters. Purely client side: the whole list is already loaded, so
                  narrowing it never costs a round trip. */}
              <div className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
                    <Input
                      placeholder="Search exams by title, course, or checkpoint..."
                      value={examSearch}
                      onChange={(e) => setExamSearch(e.target.value)}
                      aria-label="Search exams"
                      className="pl-9 h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-sm"
                    />
                  </div>

                  <Select value={examStatusFilter} onValueChange={setExamStatusFilter}>
                    <SelectTrigger
                      aria-label="Filter exams by status"
                      className="h-10 w-full sm:w-44 rounded-xl bg-muted/20 border-muted/70 focus:ring-primary text-sm"
                    >
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      {examStatusFilterDefs.map((def) => (
                        <SelectItem key={def.key} value={def.key}>
                          {def.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={examTypeFilter} onValueChange={setExamTypeFilter}>
                    <SelectTrigger
                      aria-label="Filter exams by type"
                      className="h-10 w-full sm:w-48 rounded-xl bg-muted/20 border-muted/70 focus:ring-primary text-sm"
                    >
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {examSectionDefs.map((def) => (
                        <SelectItem key={def.key} value={def.key}>
                          {def.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {hasExamFilters && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearExamFilters}
                      className="h-10 px-3 rounded-xl text-xs text-muted-foreground hover:text-destructive shrink-0"
                    >
                      <FilterX className="h-3.5 w-3.5 mr-1.5" />
                      Clear
                    </Button>
                  )}
                </div>

                {hasExamFilters && (
                  <p className="text-xs text-muted-foreground">
                    Showing {filteredExams.length} of {availableExams.length} exam
                    {availableExams.length === 1 ? "" : "s"}
                  </p>
                )}
              </div>

              {filteredExams.length === 0 && (
                <Card className="py-16 text-center space-y-4">
                  <Search className="h-16 w-16 mx-auto text-muted-foreground/30" />
                  <div>
                    <h3 className="text-lg font-semibold">No Matching Exams</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      No exam matches your current search and filters.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearExamFilters}
                    className="mx-auto"
                  >
                    <FilterX className="h-3.5 w-3.5 mr-1.5" />
                    Clear Filters
                  </Button>
                </Card>
              )}

              {examSections.map((section) => (
                <section key={section.key} className="space-y-4">
                  <div className="flex items-center gap-3 border-b pb-2">
                    <h2 className="text-xl font-bold tracking-tight">{section.title}</h2>
                    <Badge variant="secondary" className="ml-auto">
                      {section.exams.length}
                    </Badge>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {section.exams.map((exam) => {
                      const isPassed = exam.already_passed;
                      const isCompleted = exam.is_completed;
                      const isPendingGrading = exam.exam_status === "pending_grading";
                      const gradedEssays =
                        exam.last_total_essay_count - exam.last_pending_essay_count;
                      const attemptCount = exam.attempts_taken;
                      // null = unlimited (PracticeQuiz). For every other exam type this is
                      // 1 automatic attempt plus any admin-granted retakes, minus attempts used.
                      const remainingAttempts = exam.remaining_attempts;
                      const hasUnlimitedAttempts = remainingAttempts == null;
                      const isLockedOut = exam.is_locked;
                      const isExamTrainingComplete = exam.is_ready;
                      const checkpointModulesCount = exam.total_modules;
                      const checkpointCompletedCount = exam.completed_modules;
                      // An in-video checkpoint gates on a playback timestamp, not on whole modules,
                      // so it legitimately reports 0 gating modules. Only render the x/y counter when
                      // there is a real module barrier to count — otherwise the button read
                      // "Complete Required Training (0/0)", which told the student nothing.
                      const hasModuleBarrier = checkpointModulesCount > 0;
                      // Distinct from an ordinary "not reached yet" lock: the student FAILED a prior
                      // attempt and must rewatch the covered videos before retrying (#1, #7).
                      const isRemedial = exam.requires_remedial_rewatch;

                      // Client-side authoritative attempt ceiling. Computed independently of is_locked
                      // so the button is disabled and routing is blocked even if the server-derived
                      // lock flag is momentarily stale (e.g. after a very fast second attempt).
                      // The server is the ultimate source of truth and will reject the API call
                      // regardless, but this is a defense-in-depth layer that prevents navigation.
                      const attemptsExhausted =
                        !exam.can_resume && !hasUnlimitedAttempts && remainingAttempts <= 0;

                      // ── Card state discriminants ───────────────────────────────────────────────
                      // Every branch below keys off the server's lock_reason_code, which names the ONE
                      // rule that actually blocked the start. The previous version derived all of them
                      // from is_ready (= !is_locked), a single aggregate flag that is equally true for
                      // a spent attempt, a closed window and an unreached prerequisite. The result was
                      // that every locked exam rendered "Prerequisite Required" and told the student
                      // to complete training they may already have finished — and the reopen branch
                      // below was unreachable, because it required is_ready and is_locked at once.
                      const lockCode = exam.lock_reason_code;

                      // Progression gate: the student has genuinely not worked up to this exam yet.
                      // Remedial is a sub-case of it and is surfaced separately (its own badge/copy).
                      const isPrerequisiteLocked =
                        isLockedOut && lockCode === "ProgressionLocked" && !isRemedial;

                      // The attempt is spent and no grant remains — only a Team Leader/Admin reopen
                      // can return it. attemptsExhausted is kept as a client-side fallback for the
                      // brief window where the card is stale but the ceiling is already breached.
                      const isAdminReopenNeeded =
                        !exam.can_resume &&
                        (lockCode === "AttemptsExhausted" ||
                          (lockCode == null && attemptsExhausted));

                      // Locks that are nobody's fault and need no call to action — just an accurate
                      // label. Previously all three were mislabelled as a prerequisite problem.
                      const isWindowNotOpen = isLockedOut && lockCode === "WindowNotOpen";
                      const isWindowClosed = isLockedOut && lockCode === "WindowClosed";
                      const isNotEnrolled = isLockedOut && lockCode === "NotEnrolled";

                      return (
                        <Card
                          key={`${exam.exam_id}-${exam.course_id}-${exam.checkpoint_id}`}
                          className="flex flex-col hover:shadow-lg transition-shadow relative"
                        >
                          <CardHeader>
                            <div className="flex justify-between items-start mb-2">
                              <div className="space-y-1">
                                <CardTitle className="text-lg font-semibold leading-snug">
                                  {exam.title}
                                </CardTitle>
                                {(exam.course_title || exam.checkpoint_title) && (
                                  <p className="text-xs text-primary font-medium">
                                    {exam.course_title}{" "}
                                    {exam.checkpoint_title ? `— ${exam.checkpoint_title}` : ""}
                                  </p>
                                )}
                              </div>
                              {isPendingGrading && !exam.has_active_session && (
                                <Badge
                                  variant="outline"
                                  className="border-indigo-500 text-indigo-500 bg-indigo-500/5 gap-1"
                                >
                                  <Clock className="h-3 w-3" />
                                  Pending Grading
                                </Badge>
                              )}
                              {/* Three outcomes, not two. `isPassed` answers the gating question, so
                                  it is false both for a student who failed and for one whose pass was
                                  reset — and calling the second one "Failed", in red, over the 100%
                                  their own history still shows, is simply untrue. The reset case gets
                                  its own neutral badge; the banner further down explains it. */}
                              {isCompleted && !isPendingGrading && !exam.has_active_session && (
                                <Badge
                                  variant={
                                    isPassed
                                      ? "default"
                                      : pastSessions[exam.exam_id]?.superseded_pass_at
                                        ? "outline"
                                        : "destructive"
                                  }
                                  className={
                                    !isPassed && pastSessions[exam.exam_id]?.superseded_pass_at
                                      ? "border-amber-500 text-amber-600 bg-amber-500/5"
                                      : undefined
                                  }
                                >
                                  {isPassed
                                    ? "Passed"
                                    : pastSessions[exam.exam_id]?.superseded_pass_at
                                      ? "Pass Reset"
                                      : "Failed"}
                                </Badge>
                              )}
                              {exam.has_active_session && (
                                <Badge
                                  variant="outline"
                                  className="border-amber-500 text-amber-500 bg-amber-500/5 animate-pulse"
                                >
                                  In Progress
                                </Badge>
                              )}
                              {/* One badge per lock category, chosen in the same precedence order the
                                  server evaluates them. Gated on isLockedOut (not on the individual
                                  flags) so a lock can never render with no badge at all. */}
                              {isLockedOut &&
                                !isCompleted &&
                                !exam.has_active_session &&
                                (isRemedial ? (
                                  <Badge
                                    variant="outline"
                                    className="border-destructive/40 text-destructive gap-1"
                                  >
                                    <Lock className="h-3 w-3" /> Rewatch Required
                                  </Badge>
                                ) : isPrerequisiteLocked ? (
                                  <Badge
                                    variant="outline"
                                    className="border-amber-500/60 text-amber-600 dark:text-amber-400 gap-1"
                                  >
                                    <Lock className="h-3 w-3" /> Prerequisite Required
                                  </Badge>
                                ) : isAdminReopenNeeded ? (
                                  <Badge
                                    variant="outline"
                                    className="border-destructive/40 text-destructive gap-1"
                                  >
                                    <Lock className="h-3 w-3" /> Reopen Required
                                  </Badge>
                                ) : isWindowNotOpen ? (
                                  <Badge
                                    variant="outline"
                                    className="border-muted-foreground/30 text-muted-foreground gap-1"
                                  >
                                    <Clock className="h-3 w-3" /> Not Open Yet
                                  </Badge>
                                ) : isWindowClosed ? (
                                  <Badge
                                    variant="outline"
                                    className="border-muted-foreground/30 text-muted-foreground gap-1"
                                  >
                                    <Lock className="h-3 w-3" /> Window Closed
                                  </Badge>
                                ) : isNotEnrolled ? (
                                  <Badge
                                    variant="outline"
                                    className="border-muted-foreground/30 text-muted-foreground gap-1"
                                  >
                                    <Lock className="h-3 w-3" /> Not Enrolled
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="border-muted-foreground/30 text-muted-foreground gap-1"
                                  >
                                    <Lock className="h-3 w-3" /> Locked
                                  </Badge>
                                ))}
                            </div>
                            <CardDescription className="line-clamp-2 min-h-[40px]">
                              {exam.description || "No description available."}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="mt-auto space-y-4 pt-0">
                            <div className="grid grid-cols-2 gap-2 border-t border-b py-3 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5" />
                                <span>{exam.time_limit_minutes} mins</span>
                              </div>
                              <div className="flex items-center gap-1.5 justify-end">
                                <Timer className="h-3.5 w-3.5" />
                                <span>Pass: {exam.passing_score}%</span>
                              </div>
                            </div>

                            {(exam.start_date || exam.end_date) && (
                              <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                                {exam.start_date && (
                                  <div className="flex items-center gap-1.5">
                                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                                    <span>Opens: {new Date(exam.start_date).toLocaleString()}</span>
                                  </div>
                                )}
                                {exam.end_date && (
                                  <div className="flex items-center gap-1.5">
                                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                                    <span>Closes: {new Date(exam.end_date).toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Only meaningful while a progression rule is what's blocking — for a
                                spent attempt or a closed window the "next required module" is stale
                                context that reads as the reason, which it isn't. */}
                            {exam.next_required_module && (isPrerequisiteLocked || isRemedial) && (
                              <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 p-2.5 rounded-lg flex items-start gap-1.5 font-sans">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                <div>
                                  <span className="font-semibold">Next required:</span>{" "}
                                  {exam.next_required_module}
                                </div>
                              </div>
                            )}

                            {/* A pass this student earned and staff then voided by authorizing a new
                                attempt over it. Shown only while it is still voided: `already_passed`
                                answers the gating question, so it is false here for a student who
                                never passed AND for one whose pass was reset — and the card carries
                                nothing else that could tell those two apart. Their own attempt
                                history, right below, still shows the score they got. */}
                            {!isPassed && pastSessions[exam.exam_id]?.superseded_pass_at && (
                              <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 p-2.5 rounded-lg flex items-start gap-1.5">
                                <RotateCcw className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                <div>
                                  <span className="font-semibold">Your earlier pass was reset.</span>{" "}
                                  Your team leader gave you a new attempt at this exam, so you need to
                                  pass it again. Your previous result is still in your history.
                                  {pastSessions[exam.exam_id]?.superseded_pass_reason
                                    ? ` Reason: ${pastSessions[exam.exam_id].superseded_pass_reason}`
                                    : ""}
                                </div>
                              </div>
                            )}

                            {isPendingGrading && (
                              <div className="text-xs bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900/50 p-2.5 rounded-lg space-y-1.5">
                                <div className="flex items-start gap-1.5 text-indigo-700 dark:text-indigo-300 font-semibold">
                                  <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                  <span>
                                    Awaiting manual grading — your essay answer
                                    {exam.last_pending_essay_count === 1 ? " is" : "s are"} under
                                    instructor review.
                                  </span>
                                </div>
                                {exam.last_total_essay_count > 0 && (
                                  <div className="flex justify-between text-muted-foreground">
                                    <span>Essays reviewed:</span>
                                    <span className="font-semibold">
                                      {gradedEssays} of {exam.last_total_essay_count}
                                    </span>
                                  </div>
                                )}
                                {exam.last_total_points != null && exam.last_total_points > 0 && (
                                  <div className="flex justify-between text-muted-foreground">
                                    <span>Auto-graded so far:</span>
                                    <span className="font-semibold">
                                      {exam.last_auto_graded_points}/{exam.last_auto_graded_total}{" "}
                                      pts
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {(attemptCount > 0 || exam.has_active_session) && (
                              <div className="bg-muted/30 p-2.5 rounded-lg text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Attempts Taken:</span>
                                  <span className="font-semibold">{attemptCount}</span>
                                </div>
                                {exam.score !== null && !isPendingGrading && (
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">
                                      Last Attempt Score:
                                    </span>
                                    <span className="font-semibold">{exam.score}%</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {attemptCount > 0 && pastSessions[exam.exam_id]?.session_id && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full mt-2 border-primary/20 hover:border-primary/50 text-xs font-semibold flex items-center justify-center gap-1.5"
                                onClick={() => {
                                  const match = activeExams.find((e) => e.id === exam.exam_id);
                                  const policy =
                                    match?.reviewPolicy ?? match?.review_policy ?? "Never";
                                  handleOpenReview(
                                    exam.exam_id,
                                    pastSessions[exam.exam_id].session_id,
                                    policy,
                                    exam.title,
                                  );
                                }}
                              >
                                <HelpCircle className="h-3.5 w-3.5" /> Review Last Attempt
                              </Button>
                            )}

                            {/* Reopen reason banner — shown when an admin reopened this session */}
                            {exam.can_resume && exam.reopen_reason && (
                              <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 p-3 mt-2 space-y-1">
                                <div className="flex items-center gap-1.5 text-blue-700 dark:text-blue-300 font-semibold text-xs">
                                  <Info className="h-4 w-4 shrink-0" />
                                  Session Reopened by Administrator
                                </div>
                                <p className="text-xs text-blue-600 dark:text-blue-400 leading-relaxed">
                                  {exam.reopen_reason}
                                </p>
                              </div>
                            )}

                            <Button
                              className="w-full"
                              disabled={
                                // isLockedOut covers EVERY server lock reason, so the button stays
                                // disabled for categories the copy above names individually (closed
                                // window, not enrolled, …). Listing the individual flags here instead
                                // would silently re-enable the button for any reason not enumerated.
                                //
                                // isPassed is deliberately NOT part of this. Whether a pass bars a new
                                // sitting is the attempt ledger's call, and the server already made
                                // it: an ordinary pass spends the attempt and comes back locked as
                                // AttemptsExhausted, while a pass a moderator granted a retake over
                                // comes back unlocked precisely so the student can sit it again.
                                // Disabling on the pass flag overrode the second case and made the
                                // granted attempt impossible to start.
                                !exam.can_resume &&
                                (isLockedOut || (isMobilePhoneUA() && !isAdmin))
                              }
                              onClick={async () => {
                                if (isMobilePhoneUA() && !isAdmin) {
                                  toast.error(
                                    "Exams cannot be taken on mobile phones. Please use a laptop, desktop, or tablet.",
                                  );
                                  return;
                                }

                                // Client-side strict attempt gate — blocks routing when the limit is
                                // reached. The server will also reject the API call, but this prevents
                                // the user from ever entering the exam runner phase.
                                if (attemptsExhausted) {
                                  toast.error(
                                    "Maximum attempt limit reached for this exam. You cannot start a new attempt.",
                                  );
                                  return;
                                }

                                if (isLockedOut && !exam.can_resume) {
                                  toast.error(exam.lock_reason ?? "This exam is locked.");
                                  return;
                                }

                                setSelectedExam({
                                  id: exam.exam_id,
                                  title: exam.title,
                                  description: exam.description,
                                  time_limit_minutes: exam.time_limit_minutes,
                                  is_biometric_required: exam.is_biometric_required,
                                  max_violations: exam.max_violations,
                                  passing_score: exam.passing_score,
                                } as any);
                                resetBiometric();

                                if (exam.can_resume && exam.session_id) {
                                  // Which resume this is decides whether the student re-proves their
                                  // identity, so it is asked of the SERVER rather than inferred from
                                  // `reopen_reason`.
                                  //
                                  // reopen_reason is a one-time banner the server clears the moment
                                  // the student resumes, and a Team Leader may reopen with no reason
                                  // at all — so "has a reason" and "was reopened" are different
                                  // questions, and the second one is the one that matters here.
                                  // Reading the first sent reopened sessions down the branch below,
                                  // which marks the student verified locally and walks straight into
                                  // the questions. That branch never calls createExamSession, so the
                                  // server's own re-verification gate — which lives in that endpoint —
                                  // was never consulted either. Four completed production sessions
                                  // got through with BiometricConfirmed still false.
                                  const resumeState = await fetchSessionState(
                                    exam.session_id,
                                  ).catch(() => null);
                                  const mustReVerify =
                                    resumeState?.requires_biometric_re_verification ?? false;

                                  if (mustReVerify) {
                                    // Reopened, and identity has not been re-proved: full biometric
                                    // preflight. The session id is held so initializeExamSession
                                    // resumes it rather than consuming another attempt.
                                    setReopenedSessionId(exam.session_id);
                                    setPhase("biometric");
                                  } else if (exam.reopen_reason) {
                                    // Reopened but not biometric-gated — still the full security
                                    // preflight (DevTools, monitors, camera).
                                    setReopenedSessionId(exam.session_id);
                                    setPhase("preflight");
                                  } else {
                                    // True mid-session resume (page refresh / connectivity drop): the
                                    // session is live and the student was already verified on it.
                                    setBiometricStatus("verified");
                                    verifiedForExamRef.current = exam.exam_id;
                                    initializeExamSession(exam.session_id);
                                  }
                                } else if (exam.is_biometric_required) {
                                  setPhase("biometric");
                                } else {
                                  setPhase("preflight");
                                }
                              }}
                            >
                              {/* "Passed" is a terminal label — it must only appear when there is
                                  genuinely nothing left to do. A standing pass locks the exam
                                  (AttemptsExhausted), so pairing it with isLockedOut keeps that
                                  reading intact; a pass with an outstanding retake grant falls
                                  through to "Retake Exam", which is what the button now does. */}
                              {isPassed && isLockedOut
                                ? "Passed"
                                : exam.can_resume
                                  ? "Resume Exam"
                                  : isPendingGrading
                                    ? "Awaiting Grading"
                                    : isRemedial
                                      ? "Rewatch Required Before Retry"
                                      : isPrerequisiteLocked
                                        ? hasModuleBarrier
                                          ? `Complete Required Training (${checkpointCompletedCount}/${checkpointModulesCount})`
                                          : "Keep Watching to Unlock"
                                        : isAdminReopenNeeded
                                          ? "Locked — Ask Your Team Leader to Reopen"
                                          : isWindowNotOpen
                                            ? "Not Open Yet"
                                            : isWindowClosed
                                              ? "Exam Window Closed"
                                              : isNotEnrolled
                                                ? "Not Enrolled in This Course"
                                                : isLockedOut
                                                  ? "Locked"
                                                  : isMobilePhoneUA() && !isAdmin
                                                    ? "Not available on mobile phones"
                                                    : isCompleted
                                                      ? "Retake Exam"
                                                      : "Start Exam"}
                              {/* The forward arrow means "this click goes somewhere" — show it only
                                    when the button is actually actionable. */}
                              {(!isLockedOut || exam.can_resume) &&
                                !(isMobilePhoneUA() && !isAdmin) && (
                                  <ArrowRight className="ml-2 h-4 w-4" />
                                )}
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PHASE 2: BIOMETRIC WEBCAM PREVIEW */}
      {phase === "biometric" && selectedExam && (
        <Card className="max-w-xl mx-auto shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-primary" />
              Biometric Selfie Check
            </CardTitle>
            <CardDescription>
              This exam is proctored. Position yourself clearly in front of your camera.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 flex flex-col items-center">
            {/* Webcam Preview Circle */}
            <div className="relative h-60 w-60 rounded-full border-4 border-muted overflow-hidden bg-black flex items-center justify-center">
              {/* The <video> stays mounted for the whole phase and the states below are overlaid on
                  it. It used to be swapped out for the error block, which detached videoRef — so a
                  recovery could remount the element with no stream attached and nothing to
                  reattach one. */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`h-full w-full object-cover scale-x-[-1] ${
                  cameraAccessError ? "invisible" : ""
                }`}
              />
              {cameraAccessError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground p-6 text-center select-none">
                  <Camera className="h-10 w-10 mb-2 text-muted-foreground/40 animate-pulse" />
                  <span className="text-xs font-semibold text-muted-foreground/85">
                    Camera Unavailable
                  </span>
                  {/* The owner's message names the actual cause — denied, busy, or an insecure origin.
                      The generic "check your permissions and your connection" fallback below covers
                      only the case where even the failure kind is unknown; using it unconditionally
                      sent students to settings that were fine, for a device another app was holding. */}
                  <span className="text-[10px] text-muted-foreground/60 mt-1 leading-normal">
                    {camera.error?.message ??
                      "Check the browser's camera permission, close any other app using the camera, then retry."}
                  </span>
                </div>
              )}
              {cameraStarting && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-center select-none">
                  <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  <span className="text-[11px] font-medium text-white/85">Starting camera…</span>
                  <span className="text-[10px] text-white/55 leading-normal px-6">
                    Approve the browser permission prompt if it appears.
                  </span>
                </div>
              )}
              {biometricScanning && (
                <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                  <div className="absolute left-0 w-full h-[3px] bg-primary shadow-[0_0_8px_var(--primary)] animate-[scan_3s_linear_infinite]" />
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}
              {biometricConfirmed && (
                <div className="absolute inset-0 bg-green-500/25 flex items-center justify-center backdrop-blur-[2px]">
                  <UserCheck className="h-16 w-16 text-green-500 animate-in zoom-in duration-300" />
                </div>
              )}
            </div>

            <style>{`
              @keyframes scan {
                0% { top: 0%; }
                50% { top: 100%; }
                100% { top: 0%; }
              }
            `}</style>

            <div className="text-center space-y-2 max-w-sm">
              <p className="text-sm font-medium">Verify Identity</p>
              <p className="text-xs text-muted-foreground">
                Your face must remain completely visible. Tab switches and screen sharing
                restrictions apply.
              </p>
            </div>

            {/* Visible, actionable error instead of a silent bounce-loop. */}
            {biometricStatus === "error" && biometricError && (
              <div className="w-full rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-center text-sm text-destructive">
                {biometricError}
              </div>
            )}

            <div className="flex gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  resetBiometric();
                  setReopenedSessionId(null);
                  setPhase("selection");
                  setSelectedExam(null);
                }}
              >
                Cancel
              </Button>
              {biometricStatus === "verified" ? (
                <Button className="flex-1" onClick={() => setPhase("preflight")}>
                  Next Step
                </Button>
              ) : cameraAccessError ? (
                // Manual retry on the shared stream. The owner already retries a busy device on its
                // own short backoff, so reaching this button means either a denial or a device the
                // student has just gone and fixed.
                <Button className="flex-1" variant="outline" onClick={handleRetryCamera}>
                  <Camera className="mr-2 h-4 w-4" />
                  Retry Camera
                </Button>
              ) : (
                <Button
                  className="flex-1"
                  disabled={biometricScanning || cameraStarting}
                  onClick={triggerSelfieScan}
                  style={{ background: "var(--gradient-brand)" }}
                >
                  {cameraStarting
                    ? "Starting camera..."
                    : biometricStatus === "capturing"
                      ? "Scanning..."
                      : biometricStatus === "uploading"
                        ? "Uploading..."
                        : biometricStatus === "error"
                          ? "Retry"
                          : "Verify & Scan"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* PHASE 3: PRE-FLIGHT TERMS & SECURITY RULES */}
      {phase === "preflight" && selectedExam && (
        <Card className="max-w-xl mx-auto shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl font-bold">
              <ShieldCheck className="h-6 w-6 text-primary" />
              Proctoring Pre-flight Instructions
            </CardTitle>
            <CardDescription>
              Please read and accept the security terms before loading the exam.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Reopen reason alert — shown when an admin reopened the session and left a message */}
            {availableExams.find(
              (ae) => ae.exam_id === selectedExam.id && ae.reopen_reason,
            )?.reopen_reason && (
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-4 space-y-1.5">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold text-sm">
                  <Info className="h-5 w-5 shrink-0" />
                  Your exam session was reopened by an administrator
                </div>
                <p className="text-sm text-emerald-600 dark:text-emerald-400 leading-relaxed pl-7">
                  <span className="font-medium">Reason: </span>
                  {availableExams.find((ae) => ae.exam_id === selectedExam.id)?.reopen_reason}
                </p>
              </div>
            )}
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-4 space-y-3">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 font-semibold text-sm">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                Strict Testing Guidelines:
              </div>
              <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-2 list-disc pl-5">
                <li>Do NOT close, minimize, or switch away from the active tab.</li>
                <li>Do NOT press Ctrl+C, Ctrl+V, or inspect element key combinations.</li>
                <li>All windows focus shifts are tracked and flagged directly to admins.</li>
                <li>Make sure your internet connection remains stable.</li>
              </ul>
            </div>

            {/* Camera readiness indicator — proactive feedback before the student clicks Start */}
            <div
              className={`rounded-lg border p-3 flex items-center gap-3 ${
                preflightCameraReady === null
                  ? "border-muted bg-muted/20"
                  : preflightCameraReady
                    ? "border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/20"
                    : "border-destructive/30 bg-destructive/10"
              }`}
            >
              {preflightCameraReady === null ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Checking camera access…</span>
                </>
              ) : preflightCameraReady ? (
                <>
                  <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                    Camera ready — webcam monitoring will be active during this exam.
                  </span>
                </>
              ) : (
                <div className="space-y-2 w-full">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-destructive shrink-0" />
                    <span className="text-xs text-destructive font-medium">
                      {preflightCameraError ?? "Camera is not available."}
                    </span>
                  </div>
                  {/* Retries the SHARED stream rather than opening a throwaway one. The old handler
                      called getUserMedia itself with a bare `video: true` — different constraints from
                      every other call site, and one more open/close cycle on a device that was most
                      likely failing because of the last one. */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => void retryCamera()}
                    disabled={cameraStarting}
                  >
                    <Camera className="h-3.5 w-3.5" />
                    {cameraStarting ? "Checking…" : "Retry Camera"}
                  </Button>
                </div>
              )}
            </div>

            <div className="text-xs text-muted-foreground leading-relaxed border p-3 rounded-lg bg-muted/20">
              By starting this exam session, you acknowledge that you are the registered owner of
              this account, and agree to the code of conduct.
            </div>

            {/* If biometric verification failed, surface it here with an explicit retake path
                instead of silently bouncing back into the camera. */}
            {(selectedExam as any).is_biometric_required && biometricStatus === "error" && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 space-y-2">
                <p className="text-sm text-destructive">
                  {biometricError ?? "Biometric verification failed."}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => {
                    resetBiometric();
                    setPhase("biometric");
                  }}
                >
                  <Camera className="h-4 w-4" /> Retake Photo
                </Button>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  resetBiometric();
                  setReopenedSessionId(null);
                  setPhase("selection");
                  setSelectedExam(null);
                }}
              >
                Back
              </Button>
              <Button
                className="flex-1"
                // Webcam monitoring is mandatory, so this stays disabled until the shared stream is
                // genuinely live — including while it is still opening. The old condition only blocked
                // on a definitive failure and let the "still checking" state through to a toast.
                //
                // Except on a reopened session, where the clock has ALREADY started: the reopen sets
                // ExpiresAt to that moment plus the time limit, so every minute spent at this screen
                // is a minute off the student's attempt. A disabled button then costs them the exam
                // rather than merely delaying it — one real attempt lost 47 of its 90 minutes between
                // the reopen and the student getting in. They are let through to the camera gate in
                // initializeExamSession, which admits a resume with a warning and hands over to the
                // grace window.
                disabled={
                  loading || biometricStatus === "uploading" || (!cameraReady && !reopenedSessionId)
                }
                onClick={() => initializeExamSession(reopenedSessionId ?? undefined)}
                style={{ background: "var(--gradient-brand)" }}
              >
                {loading ? "Starting Exam..." : "Start Exam Now"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PHASE 4: ACTIVE EXAM SCREEN */}
      {phase === "active" && selectedExam && questions.length > 0 && activeQuestion && (
        <div
          className={`exam-protected grid gap-6 lg:grid-cols-[1fr_280px] ${
            // Out of fullscreen the questions are hidden outright, not merely flagged. The violation log
            // records that it happened; this is what stops the content being readable while it does.
            //
            // The same treatment covers a lost camera, and for the same reason: an unmonitored student
            // must not be able to keep reading the paper. Blurring is what makes the camera genuinely
            // required rather than merely requested.
            isFullscreenActive && !cameraLossActive
              ? ""
              : "pointer-events-none blur-xl select-none"
          }`}
          // Belt-and-braces alongside the CSS class: a context menu on the exam content is blocked even
          // if the document-level listener is torn down by a hot reload or an error boundary.
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          {/* Anti-leak identity overlay. Rendered only during an active attempt — it exists to make a
              photograph of THIS screen attributable, and has no purpose on the result page. */}
          <ExamWatermark
            fullName={(user as any)?.full_name ?? (user as any)?.fullName ?? user?.email ?? "Student"}
            identifier={user?.email ?? user?.id ?? ""}
            ipAddress={proctorConfig.ipAddress}
          />

          {/* Live monitoring camera feed — visible as a psychological deterrent. The student sees
              their own face throughout the exam with a REC indicator, giving the strong impression
              of continuous video surveillance. The feed also serves as the frame source for the
              snapshot uploader — it must decode frames, which is why it was never display:none. */}
          <div
            className="fixed bottom-4 right-4 z-[100] pointer-events-none"
            style={{ width: 160, height: 120 }}
          >
            <video
              ref={proctorVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full rounded-lg object-cover border-2 border-red-500/70 shadow-lg"
              style={{ transform: "scaleX(-1)" }}
            />
            {/* REC indicator. Tells the truth about whether anything is actually being captured — a
                pulsing "REC" over a dead feed is the deterrent working against us, since it is exactly
                what a student would see during the failure this rework fixes. */}
            <div className="absolute top-1.5 left-2 flex items-center gap-1">
              {cameraReady ? (
                <>
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
                  </span>
                  <span className="text-[10px] font-bold text-red-500 tracking-wider select-none">
                    REC
                  </span>
                </>
              ) : (
                <span className="text-[10px] font-bold text-amber-400 tracking-wider select-none bg-black/60 px-1 rounded">
                  NO SIGNAL
                </span>
              )}
            </div>
          </div>

          {/* ── Camera lost mid-attempt ─────────────────────────────────────
              Blocks the attempt while the grace window runs. The exam clock deliberately keeps
              running: blocking is what makes the camera mandatory, but pausing the timer as well
              would turn unplugging the webcam into a way to buy thinking time. */}
          {cameraLossActive && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
              <Card className="max-w-md w-full border-destructive/40">
                <CardContent className="pt-6 space-y-4 text-center">
                  <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                    <XCircle className="h-6 w-6 text-destructive" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold text-lg">Webcam monitoring stopped</h3>
                    <p className="text-sm text-muted-foreground">
                      {camera.error?.message ??
                        "The camera is no longer available. Reconnect it to continue your exam."}
                    </p>
                  </div>

                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-3xl font-bold tabular-nums text-destructive">
                      {Math.max(0, cameraGraceLeft ?? 0)}s
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Your exam will end if the camera is not restored. The timer is still running.
                    </p>
                  </div>

                  <ul className="text-xs text-muted-foreground text-left space-y-1 list-disc pl-5">
                    <li>Reconnect your webcam if it was unplugged.</li>
                    <li>Close any other app using the camera (Teams, Zoom, Camera).</li>
                    <li>Re-allow camera access if your browser blocked it.</li>
                  </ul>

                  {/* Retrying happens automatically every few seconds; this is for the student who has
                      just fixed the problem and does not want to wait for the next attempt. */}
                  <Button
                    variant="outline"
                    className="w-full gap-1"
                    onClick={() => void retryCamera()}
                    disabled={cameraStarting}
                  >
                    <Camera className="h-4 w-4" />
                    {cameraStarting ? "Reconnecting…" : "Reconnect now"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Main Question Card Area */}
          <div className="space-y-6">
            {/* Sticky Header info */}
            <div className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b flex flex-col md:flex-row items-start md:items-center justify-between px-2 md:px-6 py-3 rounded-lg md:rounded-none -mx-4 md:mx-0 shadow-sm">
              <div className="mb-2 md:mb-0">
                <p className="font-bold truncate max-w-[200px] sm:max-w-[300px]">
                  {selectedExam.title}
                </p>
                <p className="text-xs text-muted-foreground">
                  Q {currentQuestionIndex + 1} of {questions.length}
                </p>
              </div>
              <div className="flex items-center gap-4 md:gap-6 w-full md:w-auto justify-between md:justify-end">
                <Progress
                  value={(Object.keys(selectedAnswers).length / questions.length) * 100}
                  className="w-24 md:w-32"
                />

                {/* Global Exam Timer */}
                <div
                  className={`flex items-center gap-2 font-mono font-bold text-sm md:text-lg px-3 md:px-4 py-1 md:py-1.5 rounded-full border ${examTimeRemaining < 120 ? "bg-red-50 border-red-300 text-red-600 animate-pulse" : "bg-muted"}`}
                >
                  <Timer className="h-4 w-4" />
                  {formatSeconds(examTimeRemaining)}
                </div>

                {violations > 0 && (
                  <Badge variant="destructive" className="animate-pulse gap-1">
                    <AlertTriangle className="h-3 w-3" /> {violations}/{selectedExam.max_violations}
                  </Badge>
                )}
              </div>
            </div>

            {/* Current Question */}
            <Card className="min-h-[350px] flex flex-col justify-between shadow-md">
              <CardHeader className="space-y-3 pb-3">
                <div className="flex justify-between items-center">
                  <Badge variant="outline">Category: {activeQuestion.category || "General"}</Badge>
                </div>
                <CardTitle className="text-xl font-medium leading-relaxed font-sans mt-2">
                  {activeQuestion.question_text}
                </CardTitle>
                <p className="text-xs text-muted-foreground italic">
                  {(!activeQuestion.question_type || activeQuestion.question_type === "mcq") &&
                    "Select the correct answer."}
                  {activeQuestion.question_type === "true_false" && "Select True or False."}
                  {activeQuestion.question_type === "multi_select" && "Select all that apply."}
                  {activeQuestion.question_type === "essay" && "Provide a written response."}
                  {activeQuestion.question_type === "short_answer" && "Type your answer."}
                </p>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {(!activeQuestion.question_type ||
                  activeQuestion.question_type === "mcq" ||
                  activeQuestion.question_type === "true_false") &&
                  activeQuestion.options.map((option, index) => {
                    const letter = String.fromCharCode(65 + index);
                    const isSelected = selectedAnswers[activeQuestion.id] === letter;

                    return (
                      <button
                        key={index}
                        className={`w-full text-left p-4 rounded-xl border flex items-center gap-3 transition-all hover:bg-muted/50 cursor-pointer ${
                          isSelected
                            ? "border-primary bg-primary/5 shadow-sm font-medium"
                            : "border-border bg-card"
                        }`}
                        onClick={() => handleAnswerSelect(index)}
                      >
                        <div
                          className={`h-6 w-6 rounded-full border flex items-center justify-center shrink-0 text-xs font-semibold ${
                            isSelected
                              ? "bg-primary border-primary text-primary-foreground"
                              : "border-muted text-muted-foreground"
                          }`}
                        >
                          {letter}
                        </div>
                        <span className="text-sm leading-snug">{option}</span>
                      </button>
                    );
                  })}

                {activeQuestion.question_type === "multi_select" &&
                  activeQuestion.options.map((option, index) => {
                    const letter = String.fromCharCode(65 + index);
                    const isSelected = (textAnswers[activeQuestion.id] || "")
                      .split(",")
                      .includes(letter);

                    return (
                      <button
                        key={index}
                        className={`w-full text-left p-4 rounded-xl border flex items-center gap-3 transition-all hover:bg-muted/50 cursor-pointer ${
                          isSelected
                            ? "border-primary bg-primary/5 shadow-sm font-medium"
                            : "border-border bg-card"
                        }`}
                        onClick={() => handleMultiSelect(index)}
                      >
                        <div
                          className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 text-xs font-bold ${
                            isSelected
                              ? "bg-primary border-primary text-primary-foreground"
                              : "border-muted bg-card"
                          }`}
                        >
                          {isSelected && <Check className="h-3.5 w-3.5" />}
                        </div>
                        <span className="text-sm leading-snug">{option}</span>
                      </button>
                    );
                  })}

                {activeQuestion.question_type === "essay" && (
                  <Textarea
                    rows={8}
                    value={textAnswers[activeQuestion.id] || ""}
                    onChange={(e) => handleEssayChange(activeQuestion.id, e.target.value)}
                    onBlur={() => handleEssayBlur(activeQuestion.id)}
                    placeholder="Write your answer here..."
                    className="w-full text-sm resize-none bg-muted/20"
                  />
                )}

                {activeQuestion.question_type === "short_answer" && (
                  <Input
                    value={textAnswers[activeQuestion.id] || ""}
                    onChange={(e) => handleShortAnswerChange(activeQuestion.id, e.target.value)}
                    onBlur={() => handleShortAnswerBlur(activeQuestion.id)}
                    placeholder="Type your answer..."
                    className="w-full text-sm bg-muted/20"
                  />
                )}
              </CardContent>
              <div className="p-6 border-t flex items-center justify-end">
                {currentQuestionIndex < questions.length - 1 ? (
                  <Button
                    size="sm"
                    onClick={handleNextQuestion}
                    disabled={!selectedAnswers[activeQuestion.id]}
                  >
                    Next Question
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={submitting || !selectedAnswers[activeQuestion.id]}
                    onClick={submitExam}
                    style={{ background: "var(--gradient-brand)" }}
                    className="text-primary-foreground font-semibold"
                  >
                    {submitting ? "Submitting..." : "Submit Exam"}
                    <Check className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            </Card>
          </div>

          {/* Questions Navigation Grid Sidebar */}
          <Card className="h-fit">
            <CardHeader className="pb-3 border-b">
              <CardTitle className="text-sm font-semibold">Progress Tracker</CardTitle>
              <CardDescription className="text-xs">Your exam progress</CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-6 sm:grid-cols-10 lg:grid-cols-4 gap-2">
                {questions.map((q, idx) => {
                  const isAnswered = !!selectedAnswers[q.id];
                  const isCurrent = currentQuestionIndex === idx;

                  return (
                    <div
                      key={q.id}
                      className={`h-9 w-9 rounded-lg border text-xs font-medium flex items-center justify-center transition-all select-none ${
                        isCurrent
                          ? "border-primary bg-primary text-primary-foreground font-bold ring-2 ring-primary/20"
                          : isAnswered
                            ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
                            : "border-border"
                      }`}
                    >
                      {idx + 1}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 space-y-2 text-xs text-muted-foreground border-t pt-4">
                <div className="flex items-center gap-2">
                  <div className="h-3.5 w-3.5 rounded bg-primary text-primary-foreground border border-primary shrink-0" />
                  <span>Current Question</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3.5 w-3.5 rounded bg-green-50 border border-green-200 shrink-0" />
                  <span>Answered Question</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3.5 w-3.5 rounded border border-border shrink-0" />
                  <span>Unanswered Question</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* PHASE: INESCAPABLE TERMINATION LOCKOUT */}
      {phase === "terminated" && (
        <div className="fixed inset-0 z-[9999] bg-red-950 flex flex-col items-center justify-center text-white select-none pointer-events-auto">
          <div className="animate-pulse mb-6">
            <XOctagon className="h-24 w-24 text-red-400" />
          </div>
          <h1 className="text-4xl font-black tracking-tight mb-4">Exam Terminated</h1>
          <p className="text-lg text-red-200 max-w-md text-center">
            You have exceeded the maximum allowed proctoring violations. This attempt has been
            flagged and locked.
          </p>
          <div className="mt-8 border border-red-800 bg-red-900/50 rounded-xl p-4 text-sm text-red-300 max-w-sm text-center">
            Refreshing the page will not restore your exam. This session has been permanently
            terminated.
          </div>
        </div>
      )}

      {/* PHASE 5: EXAM RESULT SUMMARY */}
      {phase === "result" && result && selectedExam && (
        <Card className="max-w-xl mx-auto shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-500">
          <div
            className="h-3.5 w-full"
            style={{
              background: result.pending_manual_grading
                ? "var(--gradient-brand, #6366f1)"
                : result.passed
                  ? "var(--gradient-success, #22c55e)"
                  : "var(--gradient-error, #ef4444)",
            }}
          />
          <CardHeader className="text-center pt-8">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4 border">
              {result.pending_manual_grading ? (
                <Clock className="h-10 w-10 text-indigo-500" />
              ) : result.passed ? (
                <CheckCircle className="h-10 w-10 text-green-500" />
              ) : (
                <XCircle className="h-10 w-10 text-red-500" />
              )}
            </div>
            <CardTitle className="text-2xl font-extrabold tracking-tight">
              {result.pending_manual_grading
                ? "Submitted — Pending Review"
                : result.passed
                  ? "Congratulations!"
                  : "Exam Not Passed"}
            </CardTitle>
            <CardDescription className="text-sm">
              You completed the {selectedExam.title} final attempt.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 px-8 pb-8 text-center">
            {/* Round Score Widget */}
            <div className="inline-block py-6 px-10 rounded-2xl bg-muted/40 border">
              <p className="text-sm text-muted-foreground">Your Score</p>
              {result.pending_manual_grading ? (
                <p className="text-3xl font-black mt-1 text-indigo-500">Pending</p>
              ) : (
                <p
                  className={`text-5xl font-black mt-1 ${result.passed ? "text-green-500" : "text-red-500"}`}
                >
                  {result.score}%
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Required passing score: {selectedExam.passing_score}%
              </p>
            </div>

            <div className="text-sm max-w-xs mx-auto space-y-1">
              <p className="font-semibold text-foreground">Performance breakdown:</p>
              <p className="text-muted-foreground">
                Answered <span className="font-semibold text-foreground">{result.correct}</span> of{" "}
                <span className="font-semibold text-foreground">{result.total}</span> questions
                correctly.
              </p>
              {result.total_points > 0 && (
                <p className="text-muted-foreground">
                  Auto-graded:{" "}
                  <span className="font-semibold text-foreground">
                    {result.auto_graded_points}/{result.auto_graded_total}
                  </span>{" "}
                  points (of {result.total_points} total).
                </p>
              )}
            </div>

            {result.pending_manual_grading && (
              <div className="rounded-lg border bg-amber-500/5 text-amber-600 border-amber-500/20 p-3 text-xs flex items-center justify-center gap-2 max-w-sm mx-auto">
                <Clock className="h-4 w-4 shrink-0" />
                <span>
                  {result.pending_essay_count} essay answer(s) awaiting manual grading. Your final
                  score will be available once grading is complete.
                </span>
              </div>
            )}

            {violations > 0 && (
              <div className="rounded-lg border bg-destructive/5 text-destructive border-destructive/20 p-3 text-xs flex items-center justify-center gap-2 max-w-sm mx-auto">
                <AlertTriangle className="h-4 w-4 shrink-0 animate-pulse" />
                <span>Recorded violations during this session: {violations}</span>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setPhase("selection");
                  setSelectedExam(null);
                  setResult(null);
                }}
              >
                Back to Center
              </Button>
              {selectedExam &&
                (selectedExam.review_policy ?? selectedExam.reviewPolicy) !== "Never" && (
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={() => {
                      const policy =
                        selectedExam.review_policy ?? selectedExam.reviewPolicy ?? "Never";
                      handleOpenReview(selectedExam.id, sessionId, policy, selectedExam.title);
                    }}
                  >
                    Review Details
                  </Button>
                )}
              <Button
                className="flex-1"
                onClick={() => navigate({ to: "/training" })}
                style={{ background: "var(--gradient-brand)" }}
              >
                Go to Training
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Proctoring Warning Dialog */}
      <Dialog open={showViolationWarning} onOpenChange={setShowViolationWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-6 w-6 text-destructive animate-bounce" />
              Proctoring Security Warning
            </DialogTitle>
            <CardDescription className="text-sm leading-relaxed text-destructive/80">
              A browser focus loss / tab switch was detected. To maintain the integrity of this
              exam, you must remain focused strictly inside this browser tab.
            </CardDescription>
          </DialogHeader>
          <div className="bg-muted/40 border p-3 rounded-lg text-xs space-y-1.5 text-muted-foreground">
            <p className="font-semibold text-foreground">Important Policy:</p>
            <p>· All violations are logged permanently to your profile logs.</p>
            <p>· Excessive violations will lead to invalidation of this attempt.</p>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={() => setShowViolationWarning(false)}>
              I Understand, Continue Exam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Forcing Overlay Dialog */}
      <Dialog open={isFullscreenRequiredOpen} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-destructive animate-pulse">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              Fullscreen Required
            </DialogTitle>
            <CardDescription className="text-sm leading-relaxed text-destructive/80">
              To proceed with the exam and prevent violation flags, you must take the exam in
              fullscreen mode.
            </CardDescription>
          </DialogHeader>
          <div className="bg-muted/40 border p-3 rounded-lg text-xs text-muted-foreground">
            Clicking the button below will request fullscreen mode and let you continue.
          </div>
          <DialogFooter>
            <Button
              className="w-full text-primary-foreground font-semibold"
              style={{ background: "var(--gradient-brand)" }}
              onClick={async () => {
                await enterFullscreen();
                setIsFullscreenRequiredOpen(false);
              }}
            >
              Re-enter Full Screen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device blocked dialog — non-dismissable gate for mobile/tablet devices */}
      <Dialog open={deviceBlocked} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <XOctagon className="h-6 w-6 text-destructive" />
              Device Not Supported
            </DialogTitle>
            <CardDescription className="text-sm leading-relaxed text-destructive/80">
              {deviceBlockReason ??
                "Exams can only be taken on a laptop or desktop computer."}
            </CardDescription>
          </DialogHeader>
          <div className="bg-muted/40 border p-3 rounded-lg text-xs text-muted-foreground">
            Please switch to a laptop or desktop computer with a screen width of at least 1024
            pixels, then navigate back to this exam.
          </div>
        </DialogContent>
      </Dialog>

      {/* Secondary monitor dialog — deliberately worded and titled for the actual condition. This
          exists because an external display used to surface as "Developer Tools Detected", which
          told the student to do something that would not have helped. */}
      <Dialog open={secondaryMonitorBlocked} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Monitor className="h-6 w-6 text-destructive" />
              Secondary Monitor Detected
            </DialogTitle>
            <CardDescription className="text-sm leading-relaxed">
              Please disconnect all external screens before taking the exam.
            </CardDescription>
          </DialogHeader>
          <div className="bg-muted/40 border p-3 rounded-lg text-xs text-muted-foreground">
            Unplug any external monitor, projector or docking station, then re-check below. Only the
            display running this exam may be connected.
          </div>
          <DialogFooter>
            <Button
              className="w-full text-primary-foreground font-semibold"
              style={{ background: "var(--gradient-brand)" }}
              onClick={() => {
                if (isSecondaryMonitorActive()) {
                  toast.error("An external screen is still connected. Please disconnect it first.");
                  return;
                }
                setSecondaryMonitorBlocked(false);
                toast.success("No external screens detected. You may continue.");
              }}
            >
              I've Disconnected External Screens — Re-check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DevTools detected dialog — blocks until DevTools is closed */}
      <Dialog open={devToolsBlocked} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-destructive animate-pulse">
              <ShieldAlert className="h-6 w-6 text-destructive" />
              Developer Tools Detected
            </DialogTitle>
            <CardDescription className="text-sm leading-relaxed text-destructive/80">
              Developer tools must be closed before you can start the exam. This attempt has been
              recorded as a security event.
            </CardDescription>
          </DialogHeader>
          <div className="bg-muted/40 border p-3 rounded-lg text-xs text-muted-foreground">
            Close all developer tool panels (F12, Inspect Element, etc.) and try again.
          </div>
          <DialogFooter>
            <Button
              className="w-full text-primary-foreground font-semibold"
              style={{ background: "var(--gradient-brand)" }}
              onClick={async () => {
                const stillOpen = await probeDevTools();
                if (stillOpen) {
                  toast.error("Developer tools are still open. Please close them first.");
                } else {
                  setDevToolsBlocked(false);
                  toast.success("Developer tools closed. You may now start the exam.");
                }
              }}
            >
              I've Closed Developer Tools — Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EXAM REVIEW DIALOG */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Exam Attempt Review - {reviewExamTitle}</DialogTitle>
            <DialogDescription>Review your performance and grading breakdown.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto my-2 pr-1 space-y-4">
            {reviewLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : !review ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Could not retrieve review data.
              </div>
            ) : (
              <div className="space-y-6">
                {/* Proctoring termination banner — shown when the attempt was auto-ended by anti-cheat. */}
                {review.terminated && (
                  <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3">
                    <ShieldAlert className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-red-600">Exam Terminated</p>
                      <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
                        This attempt was automatically ended and marked as{" "}
                        <span className="font-semibold">Failed</span> due to exceeding the maximum
                        allowed proctoring violations (e.g. screen switching, leaving the exam
                        window, or other unauthorized actions).
                      </p>
                    </div>
                  </div>
                )}

                {/* Score Widget */}
                {(() => {
                  const isPending = review.status === "PendingGrading";
                  const scoreColor = isPending
                    ? "text-indigo-500"
                    : review.passed
                      ? "text-green-500"
                      : "text-red-500";
                  return (
                    <div className="p-4 rounded-xl border bg-muted/30 grid grid-cols-1 sm:grid-cols-2 gap-4 text-center">
                      <div className="border-b pb-4 sm:border-b-0 sm:pb-0 sm:border-r sm:pr-2">
                        <p className="text-xs text-muted-foreground uppercase font-semibold">
                          Your Score
                        </p>
                        <p className={`text-3xl font-extrabold mt-1 ${scoreColor}`}>
                          {isPending
                            ? "Pending"
                            : review.score !== null
                              ? `${review.score}%`
                              : "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase font-semibold">
                          Status
                        </p>
                        <p className={`text-lg font-bold mt-2 ${scoreColor}`}>
                          {review.terminated
                            ? "TERMINATED"
                            : isPending
                              ? "PENDING GRADING"
                              : review.passed
                                ? "PASSED"
                                : "FAILED"}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Pending manual-grading explainer + essay progress */}
                {review.status === "PendingGrading" && (
                  <div className="rounded-xl border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50 dark:bg-indigo-950/20 p-4 flex items-start gap-3">
                    <Clock className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
                    <div className="space-y-1 text-sm">
                      <p className="font-semibold text-indigo-700 dark:text-indigo-300">
                        Your exam was submitted successfully and is awaiting manual grading.
                      </p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Auto-graded questions are already scored. Your essay answer
                        {(() => {
                          const essays = (review.questions ?? []).filter(
                            (q: any) => q.question_type === "Essay" || q.question_type === "essay",
                          );
                          const graded = essays.filter(
                            (q: any) => q.grading_status !== "PendingManualGrade",
                          ).length;
                          return essays.length === 1
                            ? " is awaiting instructor review."
                            : `s are awaiting instructor review — ${graded} of ${essays.length} reviewed so far.`;
                        })()}
                      </p>
                    </div>
                  </div>
                )}

                {/* Gated Review Content */}
                {reviewExamPolicy === "Never" ? (
                  <div className="text-center py-10 px-6 rounded-xl border border-dashed bg-muted/10">
                    <AlertTriangle className="h-10 w-10 mx-auto text-amber-500 mb-3" />
                    <p className="text-sm font-semibold text-foreground">
                      Detailed Questions Review Restricted
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto leading-relaxed">
                      Due to the security configuration (Never Show Answers) of this exam, you can
                      only view your final score. Original question text, your choices, and correct
                      solutions are hidden.
                    </p>
                  </div>
                ) : !review.available ? (
                  <div className="text-center py-10 px-6 rounded-xl border border-dashed bg-amber-500/5 text-amber-600 border-amber-500/25">
                    <Clock className="h-10 w-10 mx-auto mb-3" />
                    <p className="text-sm font-semibold">Detailed Review Unavailable</p>
                    <p className="text-xs mt-1">
                      {review.reason ||
                        "This review is currently unavailable based on the exam policy."}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-foreground border-b pb-2">
                      Attempt Question Details
                    </h3>
                    {review.questions.map((q: any, idx: number) => {
                      const isCorrect = q.is_correct === true;
                      const isIncorrect = q.is_correct === false;
                      const isEssay = q.question_type === "Essay" || q.question_type === "essay";

                      return (
                        <div
                          key={q.question_id || idx}
                          className="p-4 border rounded-xl space-y-3 bg-muted/10"
                        >
                          <div className="flex justify-between items-start gap-4">
                            <span className="text-xs font-semibold text-muted-foreground">
                              Question #{idx + 1}
                            </span>
                            <Badge
                              variant={
                                isCorrect ? "default" : isIncorrect ? "destructive" : "secondary"
                              }
                              className="text-[10px]"
                            >
                              {isCorrect
                                ? "Correct"
                                : isIncorrect
                                  ? "Incorrect"
                                  : humanizeGradingStatus(q.grading_status)}
                            </Badge>
                          </div>

                          <p className="text-sm font-medium">{q.question_text}</p>

                          {/* Options if Multiple Choice / MultiSelect */}
                          {!isEssay && q.options && q.options.length > 0 && (
                            <div className="grid gap-2 text-xs pl-2">
                              {q.options.map((opt: string) => {
                                const isUserAnswer =
                                  q.your_answer?.includes(opt) || q.your_answer === opt;
                                const isCorrectAnswer =
                                  q.correct_answers?.includes(opt) || q.correct_answers === opt;

                                return (
                                  <div
                                    key={opt}
                                    className={`p-2.5 rounded-lg border flex items-center justify-between ${
                                      isCorrectAnswer
                                        ? "bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-300 font-medium"
                                        : isUserAnswer
                                          ? "bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300"
                                          : "bg-background text-muted-foreground"
                                    }`}
                                  >
                                    <span>{opt}</span>
                                    {isCorrectAnswer && (
                                      <Check className="h-3.5 w-3.5 text-green-600" />
                                    )}
                                    {isUserAnswer && !isCorrectAnswer && (
                                      <XOctagon className="h-3.5 w-3.5 text-red-600" />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Essay / Short Answer Response text */}
                          {isEssay && (
                            <div className="text-xs space-y-1 bg-background p-3 rounded-lg border">
                              <p className="font-semibold text-muted-foreground">Your Response:</p>
                              <p className="whitespace-pre-wrap text-foreground italic">
                                {q.your_answer || "(No response submitted)"}
                              </p>
                            </div>
                          )}

                          {/* Points and notes */}
                          <div className="text-[11px] text-muted-foreground flex justify-between items-center border-t pt-2">
                            <span>
                              Points: {q.points_earned} / {q.points}
                            </span>
                            {q.grader_note && (
                              <span className="italic bg-amber-500/5 text-amber-600 px-2 py-0.5 rounded border border-amber-500/20">
                                Note: {q.grader_note}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="mt-4 border-t pt-4">
            <Button className="w-full" onClick={() => setReviewDialogOpen(false)}>
              Close Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
